import { useState, useEffect, useRef, Fragment, useMemo } from "react";
import {
  Plane,
  Hotel,
  BedDouble,
  Bus,
  Shield,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  Upload,
  Plus,
  ArrowRight,
  ArrowLeft,
  Users,
  FileText,
  Calendar,
  CreditCard,
  Copy,
  Split,
  Info,
  AlertCircle,
  Eye,
  Wallet,
} from "lucide-react";
import styles from "./SupplierPaymentWizard.module.css";
import {
  toApiPaymentType,
  toDisplayPaymentType,
} from "../../api/finance/usePaymentsApi";
import { getAccounts } from "../../api/finance/accounts";
import { useToast } from "../../context/ToastContext";

/* ===================================================================
   Constants
   =================================================================== */

const SUPPLIER_PAYMENT_TYPE_OPTIONS = [
  { value: "Outgoing Payment", label: "Outgoing Payment", icon: "↑" },
  { value: "Refund", label: "Supplier Refund", icon: "↩" },
];

const PAYMENT_STATUS_OPTIONS = [
  "Pending",
  "Completed",
  "Failed",
  "Cancelled",
  "Refunded",
];

const PAYMENT_METHOD_OPTIONS = [
  "Bank Transfer",
  "SEPA",
  "SWIFT",
  "Stripe",
];

const METHOD_TO_API_KEY = {
  "Bank Transfer": "bank_transfer",
  SEPA: "sepa",
  SWIFT: "swift",
  "Direct Debit": "direct_debit",
  Cash: "cash",
  Visa: "visa",
  Mastercard: "mastercard",
  Amex: "amex",
  Stripe: "stripe",
  PayPal: "paypal",
};

const PAYMENT_METHOD_ICONS = {
  Stripe: { icon: "S", bg: "#635bff", color: "#fff" },
  "Bank Transfer": { icon: "🏦", bg: "#e8f0fe", color: "#3b5998" },
  SEPA: { icon: "🏦", bg: "#e8f0fe", color: "#3b5998" },
  SWIFT: { icon: "🌐", bg: "#fef3c7", color: "#92400e" },
};

const getMethodIcon = (method) => {
  const m = PAYMENT_METHOD_ICONS[method];
  if (!m) return { icon: "💳", bg: "#f5f6f8", color: "#6b7280" };
  return m;
};

const fmt = (n) =>
  `€ ${Number(Math.round((n || 0) * 100) / 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return "—";
  }
};

const getProductTypeIcon = (type) => {
  switch (type) {
    case "Flight": return <Plane size={16} />;
    case "Hotel": return <Hotel size={16} />;
    case "Transfer": return <Bus size={16} />;
    case "Insurance": return <Shield size={16} />;
    default: return <FileText size={16} />;
  }
};

const getProductIconClass = (type) => {
  switch (type) {
    case "Flight": return styles.productIconFlight;
    case "Hotel": return styles.productIconHotel;
    case "Transfer": return styles.productIconTransfer;
    case "Insurance": return styles.productIconInsurance;
    default: return styles.productIconOther;
  }
};

const getBadgeClass = (type) => {
  switch (type) {
    case "Flight": return styles.badgeFlight;
    case "Hotel": return styles.badgeHotel;
    case "Transfer": return styles.badgeTransfer;
    case "Insurance": return styles.badgeInsurance;
    default: return styles.badge;
  }
};

const getStatusBadgeClass = (status) => {
  switch (status) {
    case "Pending": return styles.statusPending;
    case "Completed": return styles.statusCompleted;
    case "Failed": return styles.statusFailed;
    case "Cancelled": return styles.statusCancelled;
    case "Refunded": return styles.statusRefunded;
    default: return styles.statusPending;
  }
};

/* ===================================================================
   Build supplier list from booking components
   =================================================================== */

const buildSuppliersFromComponents = (bookingComponents, bookingSSRs = [], bookingModifications = [], bookingTravelerModifications = [], bookingAssignments = []) => {
  const groups = {};
  const ensureGroup = (name) => {
    const supplierName = (name || "").trim() || "Unknown Supplier";
    if (!groups[supplierName]) {
      groups[supplierName] = { id: supplierName, name: supplierName, products: [] };
    }
    return groups[supplierName];
  };
  (bookingComponents || []).forEach((c) => {
    const supplierName = (c.supplierName || "").trim() || "Unknown Supplier";
    if (!groups[supplierName]) {
      groups[supplierName] = { id: supplierName, name: supplierName, products: [] };
    }
    const pnrField = (c.fields || []).find(
      (f) => f.label === "PNR" || f.label === "Supplier Ref"
    );
    const pnr =
      pnrField?.value && pnrField.value !== "—"
        ? pnrField.value
        : c._raw?.supplierReference || "—";
    const pnrLabel =
      c.type === "Flight" ? "PNR" :
      c.type === "Hotel" ? "Res" :
      c.type === "Transfer" ? "Ref" : "Ref";

    const nameField = (c.fields || []).find(
      (f) =>
        f.label === "Hotel Name" ||
        f.label === "Transfer Type" ||
        f.label === "From"
    );
    const productName =
      nameField?.value && nameField.value !== "—"
        ? nameField.value
        : supplierName || c.type;

    let flightSegments = null;
    let flightRouteName = null;
    if (c.type === "Flight" && c._raw?.productDetails) {
      const d =
        typeof c._raw.productDetails === "string"
          ? JSON.parse(c._raw.productDetails)
          : c._raw.productDetails;
      const legs = d.legs || [];
      const outLegs = legs.filter((l) => l.direction === "Outbound");
      const inLegs = legs.filter((l) => l.direction === "Inbound");
      const isSplit = d.flightPricingMode === "SPLIT";
      const flightType = d.flightType || "OW";

      const buildSegment = (segLegs, dir) => {
        if (segLegs.length === 0) return null;
        const first = segLegs[0];
        const last = segLegs[segLegs.length - 1];
        return {
          direction: dir,
          airline: first.airlineName || first.airline || "",
          airlineCode: first.airlineCode || "",
          route: `${(first.departureAirport || "").toUpperCase()} → ${(last.arrivalAirport || "").toUpperCase()}`,
          date: first.departureDate || "",
          pnr:
            isSplit
              ? ((dir === "Outbound" ? d.outboundPnr : d.inboundPnr) ||
                d.airlinePnr || "")
              : d.airlinePnr || pnr,
        };
      };

      const outSeg = buildSegment(outLegs, "Outbound");
      const inSeg = buildSegment(inLegs, "Inbound");

      // Route label for the product line — shown for BOTH full and split flights.
      if (outSeg && inSeg) flightRouteName = `${outSeg.route} / ${inSeg.route}`;
      else if (outSeg) flightRouteName = outSeg.route;
      else if (inSeg) flightRouteName = inSeg.route;

      // IMPORTANT: only a SPLIT-priced flight is segmented into separately-payable
      // outbound/inbound legs. A FULL flight is a single supplier booking and stays
      // ONE product, even though it naturally has both outbound and inbound legs.
      // Keying "split" off the actual pricing mode (not "has both legs") prevents a
      // full round-trip from being wrongly broken into two payable segments.
      if (
        isSplit &&
        (flightType === "RT" || flightType === "Multi") &&
        (outSeg || inSeg)
      ) {
        flightSegments = {
          isSplit,
          flightType,
          outbound: outSeg,
          inbound: inSeg,
        };
      }
    }

    // Extract date info
    let dateInfo = "";
    if (c.type === "Flight") {
      const depField = (c.fields || []).find((f) => f.label === "Departure");
      if (depField?.value) dateInfo = depField.value;
    } else if (c.type === "Hotel") {
      const ciField = (c.fields || []).find((f) => f.label === "Check-in");
      const coField = (c.fields || []).find((f) => f.label === "Check-out");
      if (ciField?.value) dateInfo = `${ciField.value}${coField?.value ? ` - ${coField.value}` : ""}`;
    }

    const supplierRefField = (c.fields || []).find((f) => f.label === "Supplier Ref");
    const supplierRef = supplierRefField?.value && supplierRefField.value !== "—"
      ? supplierRefField.value
      : (c._raw?.supplierReference || "");

    let outboundPnr = "";
    let inboundPnr = "";
    if (c.type === "Flight" && flightSegments?.isSplit) {
      outboundPnr = flightSegments.outbound?.pnr || "";
      inboundPnr = flightSegments.inbound?.pnr || "";
    }

    // Hotel rooms + per-room supplier cost. Hotels are bought per room, so the
    // wizard can allocate a supplier payment against complete room amounts
    // (Per Room mode). Room definitions come from productDetails.rooms and the
    // per-room cost from pricing.roomPricing (netPrice = supplier cost). When no
    // per-room cost was recorded, the product cost is divided equally.
    let hotelRooms = null;
    if (c.type === "Hotel" && c._raw?.productDetails) {
      try {
        const hd =
          typeof c._raw.productDetails === "string"
            ? JSON.parse(c._raw.productDetails)
            : c._raw.productDetails;
        const roomDefs = Array.isArray(hd.rooms) && hd.rooms.length > 0
          ? hd.rooms
          : [{ id: "room-1", roomName: hd.roomName || "", roomType: hd.roomType || "", boardName: hd.boardName || "" }];
        const roomPricing = hd.pricing?.roomPricing || hd.pricing?.rooms || [];
        hotelRooms = roomDefs.map((r, idx) => {
          const rid = r.id || r.roomId || `room-${idx + 1}`;
          const rp = roomPricing.find((x) => String(x.roomId) === String(rid)) || {};
          return {
            id: rid,
            label: r.roomName || r.roomType || `Room ${idx + 1}`,
            board: r.boardName || r.boardCode || "",
            cost: Math.max(0, parseFloat(rp.netPrice ?? rp.costPrice ?? 0) || 0),
          };
        });
        const costSum = hotelRooms.reduce((s, r) => s + r.cost, 0);
        const totalCost = c.costPrice || 0;
        if (costSum <= 0 && totalCost > 0) {
          const cents = Math.round(totalCost * 100);
          const per = Math.floor(cents / hotelRooms.length);
          hotelRooms.forEach((r, i) => {
            r.cost = (per + (i === hotelRooms.length - 1 ? cents - per * hotelRooms.length : 0)) / 100;
          });
        }
      } catch {
        hotelRooms = null;
      }
    }

    let finalName = productName;
    if (c.type === "Flight" && flightRouteName) {
      finalName = flightRouteName;
    } else if (c.type === "Flight" && c._raw?.productDetails) {
      const pd = typeof c._raw.productDetails === "string" ? JSON.parse(c._raw.productDetails) : c._raw.productDetails;
      const allLegs = pd.legs || [];
      if (allLegs.length > 0) {
        const first = allLegs[0];
        const last = allLegs[allLegs.length - 1];
        const dep = (first.departureAirport || "").toUpperCase();
        const arr = (last.arrivalAirport || "").toUpperCase();
        if (dep && arr) finalName = `${dep} → ${arr}`;
      }
    }

    groups[supplierName].products.push({
      id: c.id,
      name: finalName,
      type: c.type,
      pnr,
      pnrLabel,
      supplierRef,
      outboundPnr,
      inboundPnr,
      due: c.costPrice || 0,
      maxAmount: c.costPrice || 0,
      flightSegments,
      rooms: hotelRooms,
      dateInfo,
      _raw: c._raw,
    });
  });

  // SSR costs — a fixed line payable to the parent product's supplier (not split
  // per traveler). Every active SSR is listed so it's visible in supplier payment
  // even before it has been priced — the amount due is its supplier cost (0 until
  // a cost is entered in the pricing step). Cancelled SSRs carry no supplier payment.
  (bookingSSRs || []).forEach((ssr) => {
    if ((ssr.status || "").toLowerCase() === "cancelled") return;
    const qty = parseInt(ssr.quantity || ssr.qty || 1, 10) || 1;
    const cost = Math.max(0, (parseFloat(ssr.costPrice || 0) || 0) * qty);
    // SSR.productId is the parent product's server id, which is exactly the
    // component id (component.id === component._raw.id). Match defensively.
    const parent = (bookingComponents || []).find(
      (c) => c != null && ssr.productId != null && String(c.id) === String(ssr.productId)
    );
    const label = ssr.ssrName || ssr.label || ssr.ssrCode || ssr.code || "SSR Service";
    ensureGroup(parent?.supplierName).products.push({
      id: `ssr-${ssr.id ?? ssr.itemId}`,
      name: `SSR · ${label}${qty > 1 ? ` ×${qty}` : ""}`,
      type: "SSR",
      pnr: ssr.ssrCode || ssr.code || "—",
      pnrLabel: "SSR",
      supplierRef: parent?._raw?.supplierReference || "",
      outboundPnr: "",
      inboundPnr: "",
      due: cost,
      maxAmount: cost,
      flightSegments: null,
      dateInfo: "",
      _raw: null,
    });
  });

  // Post-booking modification costs — fixed line payable to the modified product's supplier.
  (bookingModifications || []).forEach((mod) => {
    const cost = parseFloat(mod.supplierCost ?? mod.supplier_cost ?? 0) || 0;
    if (cost <= 0) return;
    const prodId = mod.productId ?? mod.product?.id ?? mod.componentId;
    const parent = (bookingComponents || []).find((c) => c.id === prodId);
    const title = mod.modificationTitle || mod.modificationType || "Modification";
    ensureGroup(parent?.supplierName).products.push({
      id: `mod-${mod.id}`,
      name: `Post-Booking · ${title}`,
      type: "Modification",
      pnr: parent?._raw?.supplierReference || "—",
      pnrLabel: "Ref",
      supplierRef: parent?._raw?.supplierReference || "",
      outboundPnr: "",
      inboundPnr: "",
      due: cost,
      maxAmount: cost,
      flightSegments: null,
      dateInfo: "",
      _raw: null,
    });
  });

  // Traveler-level post-booking actions with a supplier cost (e.g. an airline
  // name-change fee or a cancellation penalty). Link each one to a supplier the SAME
  // way SSRs are linked: the traveler's product assignment → product → supplier. The
  // cost then appears under that supplier when it's selected. When a traveler is on
  // several products we prefer a Flight (these fees are usually airline fees), then
  // fall back to the first assigned product's supplier.
  const ACTION_LABELS = {
    NAME_CORRECTION: "Name Correction",
    NAME_CHANGE: "Name Change",
    CANCEL_TRAVELER: "Traveler Cancellation",
  };
  (bookingTravelerModifications || []).forEach((mod) => {
    const cost = parseFloat(mod.supplierCost ?? mod.supplier_cost ?? 0) || 0;
    if (cost <= 0) return;

    const assignedComponents = (bookingAssignments || [])
      .filter((a) => String(a.travelerId) === String(mod.travelerId))
      .map((a) => (bookingComponents || []).find((c) => String(c.id) === String(a.productId)))
      .filter(Boolean);
    const parent =
      assignedComponents.find((c) => c.type === "Flight") || assignedComponents[0] || null;

    const title =
      mod.modificationTitle || ACTION_LABELS[mod.actionType] || mod.actionType || "Traveler Modification";
    ensureGroup(parent?.supplierName).products.push({
      id: `travmod-${mod.id}`,
      name: `Post-Booking · ${title}`,
      type: "Modification",
      pnr: parent?._raw?.supplierReference || "—",
      pnrLabel: "Ref",
      supplierRef: parent?._raw?.supplierReference || "",
      outboundPnr: "",
      inboundPnr: "",
      due: cost,
      maxAmount: cost,
      flightSegments: null,
      dateInfo: "",
      _raw: null,
    });
  });

  return Object.values(groups);
};

/* ===================================================================
   WIZARD STEPS
   =================================================================== */

const STEPS = [
  { id: 1, label: "Payment Details" },
  { id: 2, label: "Select Products & Travelers" },
  { id: 3, label: "Review & Confirm" },
];

/* ===================================================================
   SupplierPaymentWizard Component
   =================================================================== */

const SupplierPaymentWizard = ({
  open,
  onClose,
  onSave,
  bookingComponents = [],
  bookingTravelers = [],
  bookingAssignments = [],
  bookingSSRs = [],
  bookingModifications = [],
  bookingTravelerModifications = [],
  existingPayments = [],
  editData,
  saving = false,
}) => {
  const { showToast } = useToast();
  const realSuppliers = useMemo(
    () => buildSuppliersFromComponents(bookingComponents, bookingSSRs, bookingModifications, bookingTravelerModifications, bookingAssignments),
    [bookingComponents, bookingSSRs, bookingModifications, bookingTravelerModifications, bookingAssignments]
  );

  // Wizard step
  const [step, setStep] = useState(1);

  // Which product is being viewed/expanded on right panel (Step 2)
  const [viewingProductId, setViewingProductId] = useState(null);

  // Form state (Step 1)
  const [form, setForm] = useState({
    supplierId: "",
    paymentType: "Outgoing Payment",
    status: "Pending",
    method: "",
    date: new Date().toISOString().slice(0, 10),
    accountId: "",
    accountNumber: "",
    amount: "",
    reference: "",
    currency: "EUR",
    notes: "",
    // SUNSKY supplier fee logic: provider fee + who bears it (sunsky = fee on
    // top, supplier receives full amount; supplier = fee deducted from amount).
    transactionFee: "",
    feePaidBy: "sunsky",
  });

  // Accounts from API
  const [allAccounts, setAllAccounts] = useState([]);
  useEffect(() => {
    if (!open) return;
    getAccounts({ usage: "supplier", active: true, limit: 100 })
      .then((res) =>
        setAllAccounts(
          (res.data || []).map((a) => ({
            value: String(a.id),
            label: a.account_name,
            linkedPaymentMethods: a.linked_payment_methods || [],
            accountType: a.account_type,
            accountNumber: a.account_number || "",
          }))
        )
      )
      .catch(() => setAllAccounts([]));
  }, [open]);

  const accounts = allAccounts.filter((acc) => {
    if (!form.method) return true;
    const apiKey = METHOD_TO_API_KEY[form.method];
    if (!apiKey) return true;
    return (acc.linkedPaymentMethods || []).includes(apiKey);
  });

  // Products & travelers (Step 2)
  const [selected, setSelected] = useState({});
  const [expanded, setExpanded] = useState({});
  const [travelerAmounts, setTravelerAmounts] = useState({});
  const [amountErrors, setAmountErrors] = useState({});

  // Payment Allocation — hotels are bought per room, so a hotel supplier payment
  // defaults to Per Room (payment recorded against complete room amounts,
  // travelers informational only). Per Traveler keeps the classic breakdown.
  // Switching modes never changes the total payment amount — only how it is
  // divided internally.
  const [allocModes, setAllocModes] = useState({}); // productId -> 'per_room' | 'per_traveler'
  const [roomAmounts, setRoomAmounts] = useState({}); // `${pid}-room-${roomId}` -> "123.45"

  // Attachments
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);

  // Validation
  const [fieldErrors, setFieldErrors] = useState({});

  // Initialize / reset on open
  useEffect(() => {
    if (!open) return;
    if (editData) {
      const matchedSupplier = realSuppliers.find(
        (s) =>
          s.name.toLowerCase() ===
          (editData.supplier || editData.supplierName || "").toLowerCase()
      );
      setForm({
        supplierId: matchedSupplier ? matchedSupplier.id : "",
        paymentType:
          editData.paymentTypeDisplay ||
          toDisplayPaymentType(editData.paymentType) ||
          "Outgoing Payment",
        status: editData.status || "Pending",
        method: editData.method || "",
        date: editData.date || new Date().toISOString().slice(0, 10),
        accountId: editData.accountId ? String(editData.accountId) : "",
        accountNumber: editData.accountNumber || "",
        amount: editData.amount != null ? String(editData.amount) : "",
        reference: editData.reference || "",
        currency: editData.currency || "EUR",
        notes: editData.notes || "",
        transactionFee: editData.transactionFee != null ? String(editData.transactionFee) : "",
        feePaidBy: editData.feePaidBy || "sunsky",
      });
      if (matchedSupplier) {
        const sel = {};
        matchedSupplier.products.forEach((p) => {
          sel[p.id] = true;
        });
        setSelected(sel);
      }
      setStep(1);
      setAmountErrors({});
      setTravelerAmounts({});
      setExpanded({});
      setFieldErrors({});
      // Restore how this payment was divided (Per Room / Per Traveler).
      setAllocModes({});
      setRoomAmounts({});
      const alloc = editData.allocation;
      if (alloc && alloc.productId != null) {
        if (alloc.mode === "per_room") {
          setAllocModes({ [alloc.productId]: "per_room" });
          const ra = {};
          (alloc.rooms || []).forEach((r) => {
            if (r?.roomId == null) return;
            const amt = parseFloat(r.amount) || 0;
            ra[`${alloc.productId}-room-${r.roomId}`] = amt > 0 ? amt.toFixed(2) : "";
          });
          setRoomAmounts(ra);
        } else if (alloc.mode === "per_traveler") {
          setAllocModes({ [alloc.productId]: "per_traveler" });
          const ta = {};
          (alloc.travelers || []).forEach((t) => {
            if (t?.travelerId == null) return;
            const key = t.direction
              ? `${alloc.productId}-${t.direction}-${t.travelerId}`
              : `${alloc.productId}-${t.travelerId}`;
            const amt = parseFloat(t.amount) || 0;
            ta[key] = amt > 0 ? amt.toFixed(2) : "";
          });
          setTravelerAmounts(ta);
        }
      }
    } else {
      setForm({
        supplierId: "",
        paymentType: "Outgoing Payment",
        status: "Pending",
        method: "",
        date: new Date().toISOString().slice(0, 10),
        accountId: "",
        accountNumber: "",
        amount: "",
        reference: "",
        currency: "EUR",
        notes: "",
        transactionFee: "",
        feePaidBy: "sunsky",
      });
      setSelected({});
      setExpanded({});
      setTravelerAmounts({});
      setAmountErrors({});
      setAllocModes({});
      setRoomAmounts({});
      setStep(1);
      setFieldErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editData]);

  // What was already paid, derived from earlier COMPLETED supplier payments.
  // Payments that stored an allocation additionally give a per-room / per-traveler
  // paid figure; legacy payments without one only count at supplier level.
  const paidByAllocation = useMemo(() => {
    const rooms = {};
    const travelers = {};
    const products = {};
    (existingPayments || []).forEach((p) => {
      if ((p.status || "").toLowerCase() !== "completed") return;
      if ((p.paymentType || "") !== "outgoing_payment") return;
      if (editData?.id && p.id === editData.id) return;
      const a = p.allocation;
      if (!a || a.productId == null) return;
      const pid = String(a.productId);
      products[pid] = (products[pid] || 0) + (parseFloat(p.amount) || 0);
      (a.rooms || []).forEach((r) => {
        if (r?.roomId == null) return;
        const k = `${pid}-room-${r.roomId}`;
        rooms[k] = (rooms[k] || 0) + (parseFloat(r.amount) || 0);
      });
      (a.travelers || []).forEach((t) => {
        if (t?.travelerId == null) return;
        const k = t.direction ? `${pid}-${t.direction}-${t.travelerId}` : `${pid}-${t.travelerId}`;
        travelers[k] = (travelers[k] || 0) + (parseFloat(t.amount) || 0);
      });
    });
    return { rooms, travelers, products };
  }, [existingPayments, editData]);

  if (!open) return null;

  /* ── Helpers ── */

  const set = (k, v) => {
    setForm((p) => {
      const next = { ...p, [k]: v };
      if (k === "method") { next.accountId = ""; next.accountNumber = ""; }
      if (k === "accountId") {
        const selectedAcc = accounts.find((a) => a.value === v);
        next.accountNumber = selectedAcc?.accountNumber || "";
      }
      return next;
    });
    if (fieldErrors[k])
      setFieldErrors((p) => {
        const n = { ...p };
        delete n[k];
        return n;
      });
  };

  const showAccountNumber = form.method === "Bank Transfer" || form.method === "SEPA" || form.method === "SWIFT";
  const accountHasNumber = !!form.accountId && !!form.accountNumber;

  const activeSupplier = realSuppliers.find((s) => s.id === form.supplierId);
  const products = activeSupplier?.products || [];

  const getTravelersForProduct = (productId) => {
    const assigned = (bookingAssignments || []).filter(
      (a) => a.productId === productId
    );
    const product = products.find((p) => p.id === productId);
    const travelerPricing = product?._raw?.productDetails?.pricing?.travelerPricing || [];
    return assigned
      .map((a) => {
        const t = (bookingTravelers || []).find((tr) => tr.id === a.travelerId);
        if (!t) return null;
        const tp = travelerPricing.find((tp) => tp.travelerId === a.travelerId);
        return { ...t, assignmentId: `${productId}-${t.id}`, ticketNumber: tp?.ticketNumber || "" };
      })
      .filter(Boolean);
  };

  /* ── Per-traveler supplier cost ──
     A booking rarely costs the same for every passenger — an adult, a child and
     an infant on the same flight each have their own purchase price, and that is
     exactly what was entered in the booking's pricing step. Read those amounts
     back per traveler instead of dividing the product total, so the wizard shows
     what the supplier actually charges for each person.

     Exact costs exist whenever the product stores per-traveler pricing rows:
     flights (netPrice + tax, per direction when the flight is split-priced) and
     generic products such as Insurance / Car Rental / Visa (costPrice). When a
     product carries ONE total cost with no per-traveler breakdown — transfers,
     hotels priced per room, SSR lines, post-booking modifications — there is
     nothing to read back, and only then is an equal division the right default.

     `prefix` is the flight direction bucket: "out" | "in" | null (whole product).
  */
  const LEG_TYPE_BY_PREFIX = { out: "Outbound", in: "Inbound" };

  const getTravelerCostInfo = (product, travelers, prefix = null, fallbackTotal = null) => {
    const total = fallbackTotal != null ? fallbackTotal : product?.maxAmount || 0;
    const perPerson =
      travelers.length > 0 ? Math.round((total / travelers.length) * 100) / 100 : 0;

    let details = product?._raw?.productDetails;
    if (typeof details === "string") {
      try { details = JSON.parse(details); } catch { details = null; }
    }
    const rows = Array.isArray(details?.pricing?.travelerPricing)
      ? details.pricing.travelerPricing
      : [];

    // A flight's supplier cost per traveler is netPrice + tax (the same sum the
    // booking totals use); every other per-traveler product stores costPrice.
    const rowCost = (r) =>
      product?.type === "Flight"
        ? (parseFloat(r.netPrice || 0) || 0) + (parseFloat(r.tax || 0) || 0)
        : parseFloat(r.costPrice != null ? r.costPrice : r.netPrice || 0) || 0;

    const wantLeg = LEG_TYPE_BY_PREFIX[prefix] || null;
    // Per-traveler amounts can only be read back when the rows exist AND are shaped
    // like what's being rendered: asking for one direction of a split flight needs
    // rows that carry a direction. Rows without any legType are whole-flight rows,
    // so a per-direction view of them has nothing exact to show and divides instead.
    const exact =
      travelers.length > 0 &&
      rows.length > 0 &&
      (!wantLeg || rows.some((r) => r.legType));

    const costs = {};
    travelers.forEach((t) => {
      if (!exact) { costs[t.id] = perPerson; return; }
      const mine = rows.filter(
        (r) =>
          String(r.travelerId) === String(t.id) &&
          (wantLeg ? (r.legType || null) === wantLeg : true)
      );
      // A traveler with no priced row here genuinely carries no cost for it — an
      // unpriced passenger, or a direction whose pricing was removed when the leg
      // was cancelled. Zero is the truth; an equal share would invent a debt.
      costs[t.id] = Math.round(mine.reduce((s, r) => s + rowCost(r), 0) * 100) / 100;
    });

    const costTotal =
      Math.round(travelers.reduce((s, t) => s + (costs[t.id] || 0), 0) * 100) / 100;

    return {
      exact,
      perPerson,
      total: exact ? costTotal : Math.round(total * 100) / 100,
      costOf: (t) => costs[t.id] || 0,
    };
  };

  /* ── Payment Allocation helpers ── */

  // Only hotels with room data get the Per Room / Per Traveler choice; hotels
  // default to Per Room because hotel pricing is also structured per room.
  const hasRoomAllocation = (p) => p?.type === "Hotel" && (p.rooms || []).length > 0;
  const getAllocMode = (p) =>
    hasRoomAllocation(p) ? allocModes[p.id] || "per_room" : "per_traveler";

  // Divide `total` over weighted rows, cent-exact — the last row absorbs the
  // rounding remainder so the sum is always exactly `total`.
  const distributeCents = (total, weights) => {
    if (!weights.length) return [];
    const cents = Math.round((total || 0) * 100);
    const wSum = weights.reduce((s, w) => s + w, 0);
    const out = weights.map((w) =>
      wSum > 0 ? Math.floor((cents * w) / wSum) : Math.floor(cents / weights.length)
    );
    const used = out.reduce((s, v) => s + v, 0);
    out[out.length - 1] += cents - used;
    return out.map((v) => v / 100);
  };

  const initRoomAmounts = (product, total) => {
    const rooms = product.rooms || [];
    const shares = distributeCents(
      total,
      rooms.map((r) => (r.cost > 0 ? Math.round(r.cost * 100) : 1))
    );
    const next = {};
    rooms.forEach((r, i) => {
      next[`${product.id}-room-${r.id}`] = shares[i] > 0 ? shares[i].toFixed(2) : "";
    });
    setRoomAmounts(next);
  };

  const updateReferenceFromSelected = (selectedMap) => {
    const selectedProductIds = Object.keys(selectedMap).filter((k) => selectedMap[k]);
    if (selectedProductIds.length === 0) {
      set("reference", "");
      return;
    }
    const selectedProds = products.filter((p) => selectedMap[p.id]);
    const refs = [];
    selectedProds.forEach((p) => {
      if (p.type === "Flight" && p.flightSegments?.isSplit) {
        if (p.outboundPnr) refs.push(`Outbound PNR: ${p.outboundPnr}`);
        if (p.inboundPnr) refs.push(`Inbound PNR: ${p.inboundPnr}`);
        if (!p.outboundPnr && !p.inboundPnr && p.supplierRef) refs.push(p.supplierRef);
      } else {
        const ref = p.supplierRef || p.pnr;
        if (!ref || ref === "—") return;
        if (selectedProds.length > 1) {
          const label = p.type === "Flight" ? "PNR" : p.type === "Hotel" ? "Res" : "Ref";
          refs.push(`${p.name} (${label}: ${ref})`);
        } else {
          refs.push(ref);
        }
      }
    });
    set("reference", [...new Set(refs)].join(", "));
  };

  const toggleProduct = (pid) => {
    setSelected((prev) => {
      const wasSelected = !!prev[pid];
      if (wasSelected) {
        setExpanded((e) => { const ne = { ...e }; delete ne[pid]; return ne; });
        updateReferenceFromSelected({});
        setForm((f) => ({ ...f, amount: "" }));
        setTravelerAmounts({});
        setRoomAmounts({});
        setAllocModes({});
        setAmountErrors({});
        return {};
      }
      const next = { [pid]: true };
      setExpanded({ [pid]: true });
      updateReferenceFromSelected(next);
      const prod = products.find((pr) => pr.id === pid);
      const amt = prod?.due || 0;
      setForm((f) => ({ ...f, amount: amt > 0 ? amt.toFixed(2) : "" }));

      // Hotels default to Per Room allocation — the payment is recorded against
      // complete room amounts, not divided over travelers. When earlier payments
      // recorded per-room allocations, default to each room's REMAINING amount
      // instead of its full cost, so a second partial payment starts correct.
      if (prod && hasRoomAllocation(prod)) {
        setAllocModes({ [pid]: "per_room" });
        const roomPaid = prod.rooms.map(
          (r) => Math.round((paidByAllocation.rooms[`${pid}-room-${r.id}`] || 0) * 100) / 100
        );
        const hasPaidHistory = roomPaid.some((v) => v > 0);
        if (hasPaidHistory) {
          const amounts = {};
          let outSum = 0;
          prod.rooms.forEach((r, i) => {
            const remaining = Math.max(0, Math.round((r.cost - roomPaid[i]) * 100) / 100);
            outSum += remaining;
            amounts[`${pid}-room-${r.id}`] = remaining > 0 ? remaining.toFixed(2) : "";
          });
          outSum = Math.round(outSum * 100) / 100;
          setRoomAmounts(amounts);
          setForm((f) => ({ ...f, amount: outSum > 0 ? outSum.toFixed(2) : "" }));
        } else {
          initRoomAmounts(prod, amt);
        }
        setTravelerAmounts({});
        setAmountErrors({});
        return next;
      }

      setAllocModes({});
      setRoomAmounts({});
      // Pre-fill each traveler with the cost actually priced for them; the equal
      // division is only a fallback for products that hold a single total cost.
      const travelers = getTravelersForProduct(pid);
      if (travelers.length > 0 && amt > 0) {
        const newAmounts = {};
        const segs = prod?.flightSegments;
        const fill = (key, value) => {
          newAmounts[key] = value > 0 ? value.toFixed(2) : "";
        };
        if (segs && segs.outbound && segs.inbound) {
          const legFallback = amt / 2;
          const outInfo = getTravelerCostInfo(prod, travelers, "out", legFallback);
          const inInfo = getTravelerCostInfo(prod, travelers, "in", legFallback);
          travelers.forEach((t) => {
            fill(`${pid}-out-${t.id}`, outInfo.costOf(t));
            fill(`${pid}-in-${t.id}`, inInfo.costOf(t));
          });
        } else {
          const info = getTravelerCostInfo(prod, travelers, null, amt);
          travelers.forEach((t) => fill(`${pid}-${t.id}`, info.costOf(t)));
        }
        setTravelerAmounts(newAmounts);
        setAmountErrors({});
      }

      return next;
    });
    setViewingProductId(pid);
    if (fieldErrors.products)
      setFieldErrors((p) => {
        const n = { ...p };
        delete n.products;
        return n;
      });
  };

  const toggleExpand = (pid) => {
    if (!selected[pid]) return;
    setExpanded((p) => ({ ...p, [pid]: !p[pid] }));
  };

  const handleTravelerAmountChange = (key, value, max) => {
    setTravelerAmounts((p) => ({ ...p, [key]: value }));
    const num = parseFloat(value) || 0;
    if (max && num > max) {
      setAmountErrors((p) => ({
        ...p,
        [key]: `Max: ${fmt(max)}`,
      }));
    } else {
      setAmountErrors((p) => {
        const n = { ...p };
        delete n[key];
        return n;
      });
    }
  };

  const getProductPayAmount = (p) => {
    if (!selected[p.id]) return 0;
    if (getAllocMode(p) === "per_room") {
      const sum = (p.rooms || []).reduce(
        (s, r) => s + (parseFloat(roomAmounts[`${p.id}-room-${r.id}`]) || 0),
        0
      );
      return Math.round((sum || p.due || 0) * 100) / 100;
    }
    const travelers = getTravelersForProduct(p.id);
    if (travelers.length === 0) return p.due || 0;
    const segs = p.flightSegments;
    if (segs && (segs.outbound || segs.inbound)) {
      let sum = 0;
      travelers.forEach((t) => {
        if (segs.outbound)
          sum += parseFloat(travelerAmounts[`${p.id}-out-${t.id}`] || 0);
        if (segs.inbound)
          sum += parseFloat(travelerAmounts[`${p.id}-in-${t.id}`] || 0);
      });
      return Math.round((sum || p.due || 0) * 100) / 100;
    }
    const sum = travelers.reduce(
      (s, t) => s + (parseFloat(travelerAmounts[`${p.id}-${t.id}`]) || 0),
      0
    );
    return Math.round((sum || p.due || 0) * 100) / 100;
  };

  const handleRoomAmountChange = (key, value, max) => {
    setRoomAmounts((p) => ({ ...p, [key]: value }));
    const num = parseFloat(value) || 0;
    if (max != null && num > max) {
      setAmountErrors((p) => ({ ...p, [key]: `Max: ${fmt(max)}` }));
    } else {
      setAmountErrors((p) => {
        const n = { ...p };
        delete n[key];
        return n;
      });
    }
  };

  // Switch how the payment is divided. The TOTAL payment amount never changes —
  // the current product total is simply redistributed (pro-rata over room costs
  // for Per Room, equally over travelers for Per Traveler).
  const switchAllocMode = (product, mode) => {
    if (getAllocMode(product) === mode) return;
    const pid = product.id;
    const travelers = getTravelersForProduct(pid);
    if (mode === "per_traveler" && travelers.length === 0) {
      showToast("No travelers are assigned to this hotel — allocation stays Per Room", "error");
      return;
    }
    const total = getProductPayAmount(product);
    setAllocModes((m) => ({ ...m, [pid]: mode }));
    setAmountErrors({});
    if (mode === "per_room") {
      initRoomAmounts(product, total);
      setTravelerAmounts({});
      showToast("Allocation set to Per Room — total payment amount unchanged", "success");
    } else {
      const shares = distributeCents(total, travelers.map(() => 1));
      const next = {};
      travelers.forEach((t, i) => {
        next[`${pid}-${t.id}`] = shares[i] > 0 ? shares[i].toFixed(2) : "";
      });
      setTravelerAmounts(next);
      setRoomAmounts({});
      showToast("Allocation set to Per Traveler — total payment amount unchanged", "success");
    }
  };

  const selectedProducts = products.filter((p) => selected[p.id]);
  const totalDue = products.reduce((s, p) => s + (p.due || 0), 0);
  const totalPayment = selectedProducts.reduce(
    (s, p) => s + getProductPayAmount(p),
    0
  );
  const totalTravelers = selectedProducts.reduce((s, p) => {
    return s + getTravelersForProduct(p.id).length;
  }, 0);

  // Already paid to THIS supplier — completed outgoing payments on this booking,
  // excluding the payment currently being edited.
  const supplierAlreadyPaid = activeSupplier
    ? (existingPayments || []).reduce((s, p) => {
        if ((p.status || "").toLowerCase() !== "completed") return s;
        if ((p.paymentType || "") !== "outgoing_payment") return s;
        if (editData?.id && p.id === editData.id) return s;
        if (
          (p.supplier || "").trim().toLowerCase() !==
          (activeSupplier.name || "").trim().toLowerCase()
        )
          return s;
        return s + (parseFloat(p.amount) || 0);
      }, 0)
    : 0;

  // ── SUNSKY supplier payment fee logic (live values) ──
  //   Sunsky pays fee   → bank outgoing = amount + fee, supplier receives full amount
  //   Supplier pays fee → bank outgoing = amount, supplier receives amount − fee
  const isOutgoingType = form.paymentType === "Outgoing Payment";
  const feeNum = isOutgoingType
    ? (parseFloat(String(form.transactionFee).replace(",", ".")) || 0)
    : 0;
  const wizardAmount = parseFloat(form.amount) > 0 ? parseFloat(form.amount) : totalPayment;
  const bankOutgoing = form.feePaidBy === "supplier" ? wizardAmount : wizardAmount + feeNum;
  const netToSupplier = form.feePaidBy === "supplier" ? Math.max(0, wizardAmount - feeNum) : wizardAmount;

  const hasAmountErrors = Object.keys(amountErrors).length > 0;

  const getOutstandingClass = (total, paid) => {
    const outstanding = Math.round((total - paid) * 100) / 100;
    if (outstanding <= 0) return styles.outstandingPaid;
    if (paid > 0) return styles.outstandingPartial;
    return styles.outstandingFull;
  };

  const getStatOutstandingClass = (total, paid) => {
    const outstanding = Math.round((total - paid) * 100) / 100;
    if (outstanding <= 0) return styles.s2StatPaid;
    if (paid > 0) return styles.s2StatPartial;
    return styles.s2StatOrange;
  };

  const getSummaryOutstandingClass = (total, paid) => {
    const outstanding = Math.round((total - paid) * 100) / 100;
    if (outstanding <= 0) return styles.summaryCardValuePaid;
    if (paid > 0) return styles.summaryCardValuePartial;
    return styles.summaryCardValueOrange;
  };

  /* ── Step Validation ── */

  const validateStep1 = () => {
    const errs = {};
    if (!form.supplierId) errs.supplierId = "Supplier is required";
    if (!form.method) errs.method = "Payment method is required";
    if (form.method && !form.accountId) errs.accountId = "Account is required";
    if (showAccountNumber && !accountHasNumber && !form.accountNumber.trim()) errs.accountNumber = "Account number is required for bank transfers";
    if (!form.date) errs.date = "Payment date is required";
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      const firstErr = Object.values(errs)[0];
      showToast(firstErr, "error");
      return false;
    }
    setFieldErrors({});
    return true;
  };

  const validateStep2 = () => {
    const errs = {};
    if (selectedProducts.length === 0) {
      errs.products = "Select a product to pay";
    }
    if (!form.amount || parseFloat(form.amount) <= 0) {
      errs.amount = "Payment amount must be greater than 0";
    }
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      const firstErr = Object.values(errs)[0];
      showToast(firstErr, "error");
      return false;
    }
    if (hasAmountErrors) {
      showToast("Please fix the amount errors before proceeding", "error");
      return false;
    }
    setFieldErrors({});
    return true;
  };

  const goNext = () => {
    if (step === 1) {
      if (!validateStep1()) return;
      showToast("Payment details saved", "success");
      setStep(2);
    } else if (step === 2) {
      if (!validateStep2()) return;
      showToast("Products and travelers selected", "success");
      setStep(3);
    }
  };

  const goBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const goToStep = (s) => {
    if (s < step) {
      setStep(s);
    } else if (s === 2 && step === 1) {
      if (validateStep1()) {
        setStep(2);
      }
    } else if (s === 3 && step === 2) {
      if (validateStep2()) {
        setStep(3);
      }
    }
  };

  /* ── Submit ── */

  // How this payment is divided internally — stored with the payment so later
  // payments can show a true "Already Paid" per room / per traveler and the
  // breakdown survives into the payment views. Never changes the amount itself.
  const buildAllocationPayload = () => {
    // Edit mode selects every product of the supplier (legacy behavior), so
    // target the product the stored allocation belongs to — not blindly [0].
    const editAllocPid = editData?.allocation?.productId;
    const p =
      (editAllocPid != null &&
        selectedProducts.find((x) => String(x.id) === String(editAllocPid))) ||
      selectedProducts[0];
    if (!p) return null;
    const travelers = getTravelersForProduct(p.id);
    if (getAllocMode(p) === "per_room") {
      return {
        mode: "per_room",
        productId: p.id,
        productType: p.type,
        productName: p.name,
        rooms: (p.rooms || []).map((r) => ({
          roomId: r.id,
          label: r.label,
          amount: parseFloat(roomAmounts[`${p.id}-room-${r.id}`]) || 0,
        })),
        // Informational only — who stays in the hotel, no amounts allocated.
        travelers: travelers.map((t) => ({ travelerId: t.id, name: t.name, amount: 0 })),
      };
    }
    if (travelers.length === 0) return null;
    const segs = p.flightSegments;
    const entries = [];
    if (segs && (segs.outbound || segs.inbound)) {
      travelers.forEach((t) => {
        if (segs.outbound)
          entries.push({
            travelerId: t.id,
            name: t.name,
            direction: "out",
            amount: parseFloat(travelerAmounts[`${p.id}-out-${t.id}`]) || 0,
          });
        if (segs.inbound)
          entries.push({
            travelerId: t.id,
            name: t.name,
            direction: "in",
            amount: parseFloat(travelerAmounts[`${p.id}-in-${t.id}`]) || 0,
          });
      });
    } else {
      travelers.forEach((t) =>
        entries.push({
          travelerId: t.id,
          name: t.name,
          amount: parseFloat(travelerAmounts[`${p.id}-${t.id}`]) || 0,
        })
      );
    }
    return {
      mode: "per_traveler",
      productId: p.id,
      productType: p.type,
      productName: p.name,
      travelers: entries,
    };
  };

  // Sum of an allocation's dividing entries (rooms in per_room, travelers otherwise).
  const allocationTotal = (a) => {
    const list = a?.mode === "per_room" ? a?.rooms : a?.travelers;
    return (list || []).reduce((s, e) => s + (parseFloat(e?.amount) || 0), 0);
  };

  // Keep the stored allocation consistent with the FINAL payment amount: when the
  // user overrides Pay Amount after entering room/traveler amounts, scale the
  // entries pro-rata (cent-exact) so the breakdown always sums to the payment.
  const scaleAllocation = (a, finalAmount) => {
    if (!a) return null;
    const list = a.mode === "per_room" ? a.rooms : a.travelers;
    if (!list || !list.length) return a;
    const sum = Math.round(allocationTotal(a) * 100) / 100;
    if (sum <= 0 || Math.abs(sum - finalAmount) < 0.01) return a;
    const shares = distributeCents(
      finalAmount,
      list.map((e) => Math.round((parseFloat(e?.amount) || 0) * 100))
    );
    const scaled = list.map((e, i) => ({ ...e, amount: shares[i] }));
    return a.mode === "per_room" ? { ...a, rooms: scaled } : { ...a, travelers: scaled };
  };

  const handleConfirm = () => {
    if (hasAmountErrors) {
      showToast("Please fix amount errors before confirming", "error");
      return;
    }
    // Build payload matching what handleSaveSupplierPayment expects
    const payAmounts = {};
    selectedProducts.forEach((p) => {
      payAmounts[p.id] = String(getProductPayAmount(p));
    });

    const expandedFlights = {};
    selectedProducts.forEach((p) => {
      if (p.type === "Flight" && expanded[p.id]) {
        expandedFlights[p.id] = true;
      }
    });

    const finalAmount = parseFloat(form.amount) > 0
      ? parseFloat(form.amount)
      : totalPayment;

    // SUNSKY supplier fee rule: when the SUPPLIER bears the fee it is deducted
    // from what they receive — it can never exceed the supplier amount itself.
    if (isOutgoingType && feeNum > 0 && form.feePaidBy === "supplier" && finalAmount > 0 && feeNum > finalAmount) {
      showToast("Transaction cost cannot exceed the supplier amount when the supplier bears the cost", "error");
      return;
    }

    onSave &&
      onSave({
        ...form,
        supplierName: activeSupplier?.name || "",
        component: selectedProducts.map((p) => p.type).join(", "),
        totalAmount: finalAmount,
        amount: finalAmount,
        transactionFee: isOutgoingType ? feeNum : 0,
        feePaidBy: isOutgoingType && feeNum > 0 ? form.feePaidBy : null,
        selectedProducts,
        selected,
        payAmounts,
        travelerAmounts,
        expandedFlights,
        allocation: (() => {
          // When editing without touching the breakdown, keep the stored
          // allocation instead of overwriting it with fabricated zeros.
          let alloc = buildAllocationPayload();
          if (
            editData?.allocation &&
            allocationTotal(alloc) <= 0 &&
            allocationTotal(editData.allocation) > 0
          ) {
            alloc = editData.allocation;
          }
          return scaleAllocation(alloc, finalAmount);
        })(),
      });
  };

  const handleDraft = () => {
    showToast("Draft saved (not yet submitted)", "success");
  };

  /* ── Attachment helpers ── */
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const maxSize = 10 * 1024 * 1024; // 10MB
    const validTypes = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];
    const validFiles = [];
    files.forEach((f) => {
      if (f.size > maxSize) {
        showToast(`${f.name} exceeds 10MB limit`, "error");
      } else if (!validTypes.includes(f.type)) {
        showToast(`${f.name} — unsupported file type`, "error");
      } else {
        validFiles.push({ file: f, id: `${Date.now()}-${Math.random()}`, name: f.name, size: f.size, type: f.type });
      }
    });
    if (validFiles.length > 0) {
      setAttachments((prev) => [...prev, ...validFiles]);
      showToast(`${validFiles.length} file${validFiles.length > 1 ? "s" : ""} attached`, "success");
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
    showToast("Attachment removed", "success");
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  /* ── Amount fill helpers ── */

  // Fill each traveler's own purchase price — used when the product was priced
  // per traveler, so no division takes place at all.
  const fillExactCosts = (product, travelers, prefix, costInfo) => {
    const newAmounts = { ...travelerAmounts };
    const newErrors = { ...amountErrors };
    travelers.forEach((t) => {
      const key = prefix
        ? `${product.id}-${prefix}-${t.id}`
        : `${product.id}-${t.id}`;
      const cost = costInfo.costOf(t);
      newAmounts[key] = cost > 0 ? cost.toFixed(2) : "";
      delete newErrors[key];
    });
    setTravelerAmounts(newAmounts);
    setAmountErrors(newErrors);
    showToast("Filled each traveler's own purchase price", "success");
  };

  const splitEqually = (productId, prefix, travelers, maxPerPerson) => {
    const perPerson = maxPerPerson.toFixed(2);
    const newAmounts = { ...travelerAmounts };
    const newErrors = { ...amountErrors };
    travelers.forEach((t) => {
      const key = prefix
        ? `${productId}-${prefix}-${t.id}`
        : `${productId}-${t.id}`;
      newAmounts[key] = perPerson;
      delete newErrors[key];
    });
    setTravelerAmounts(newAmounts);
    setAmountErrors(newErrors);
    showToast(`Split ${fmt(maxPerPerson)} per traveler`, "success");
  };

  const copyToAll = (sourceKey, productId, prefix, travelers, maxPerPerson) => {
    const val = travelerAmounts[sourceKey];
    if (!val) return;
    const newAmounts = { ...travelerAmounts };
    const newErrors = { ...amountErrors };
    travelers.forEach((t) => {
      const key = prefix
        ? `${productId}-${prefix}-${t.id}`
        : `${productId}-${t.id}`;
      newAmounts[key] = val;
      const num = parseFloat(val) || 0;
      if (maxPerPerson && num > maxPerPerson)
        newErrors[key] = `Max: ${fmt(maxPerPerson)}`;
      else delete newErrors[key];
    });
    setTravelerAmounts(newAmounts);
    setAmountErrors(newErrors);
    showToast("Amount copied to all travelers", "success");
  };

  /* ── Render helpers ── */

  const renderInitials = (name) =>
    (name || "")
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  const getAvatarClass = (type) => {
    if (type === "Child") return styles.avatarChild;
    if (type === "Infant") return styles.avatarInfant;
    return styles.avatarAdult;
  };

  const getTravelerBadgeClass = (type) => {
    if (type === "Child") return styles.travelerTypeChild;
    if (type === "Infant") return styles.travelerTypeInfant;
    return styles.travelerTypeAdult;
  };

  /* ===================================================================
     STEP 1: Payment Details
     =================================================================== */

  const renderStep1 = () => (
    <div className={styles.stepContent} key="step1">
      <div className={styles.sectionTitle}>
        <span className={styles.sectionTitleIcon}>
          <CreditCard size={15} />
        </span>
        Payment Details
      </div>

      {/* Supplier */}
      {realSuppliers.length === 0 ? (
        <div className={styles.infoBanner}>
          <Info size={14} />
          <span>
            No supplier products found for this booking. Add a product with a
            supplier first.
          </span>
        </div>
      ) : (
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>
            Supplier <span className={styles.req}>*</span>
          </label>
          <select
            className={`${styles.formSelect} ${fieldErrors.supplierId ? styles.formInputError : ""}`}
            value={form.supplierId}
            onChange={(e) => {
              const suppId = e.target.value;
              set("supplierId", suppId);
              setSelected({});
              setExpanded({});
              setTravelerAmounts({});
              setAmountErrors({});
              set("reference", "");
            }}
          >
            <option value="">Select a supplier...</option>
            {realSuppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          {fieldErrors.supplierId && (
            <div className={styles.fieldError}>{fieldErrors.supplierId}</div>
          )}
        </div>
      )}

      {/* Payment Type & Status */}
      <div className={styles.formRow2}>
        <div className={`${styles.formGroup} ${styles.formGroupInRow}`}>
          <label className={styles.formLabel}>
            Payment Type <span className={styles.req}>*</span>
          </label>
          <div className={styles.selectWithIcon}>
            <span
              className={styles.typeDot}
              style={{
                background:
                  form.paymentType === "Outgoing Payment"
                    ? "#eef3ff"
                    : form.paymentType === "Refund"
                    ? "#fef3e2"
                    : "#e8f5ee",
                color:
                  form.paymentType === "Outgoing Payment"
                    ? "#2563eb"
                    : form.paymentType === "Refund"
                    ? "#b06e0a"
                    : "#0d7a4f",
              }}
            >
              {form.paymentType === "Outgoing Payment"
                ? "↑"
                : form.paymentType === "Refund"
                ? "↩"
                : "↓"}
            </span>
            <select
              className={styles.formSelect}
              value={form.paymentType}
              onChange={(e) => set("paymentType", e.target.value)}
              disabled={!!editData}
            >
              {SUPPLIER_PAYMENT_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          {editData && (
            <span className={styles.formHint}>
              Payment type cannot be changed after creation
            </span>
          )}
        </div>
        <div className={`${styles.formGroup} ${styles.formGroupInRow}`}>
          <label className={styles.formLabel}>
            Payment Status <span className={styles.req}>*</span>
          </label>
          <select
            className={styles.formSelect}
            value={form.status}
            onChange={(e) => set("status", e.target.value)}
          >
            {PAYMENT_STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Method, Date, Currency */}
      <div className={styles.formRow3}>
        <div className={`${styles.formGroup} ${styles.formGroupInRow}`}>
          <label className={styles.formLabel}>
            Payment Method <span className={styles.req}>*</span>
          </label>
          <div className={styles.selectWithIcon}>
            <span
              className={styles.methodDot}
              style={{
                background: getMethodIcon(form.method).bg,
                color: getMethodIcon(form.method).color,
              }}
            >
              {getMethodIcon(form.method).icon}
            </span>
            <select
              className={`${styles.formSelect} ${fieldErrors.method ? styles.formInputError : ""}`}
              value={form.method}
              onChange={(e) => set("method", e.target.value)}
            >
              <option value="">Select method...</option>
              {PAYMENT_METHOD_OPTIONS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          {fieldErrors.method && (
            <div className={styles.fieldError}>{fieldErrors.method}</div>
          )}
        </div>
        <div className={`${styles.formGroup} ${styles.formGroupInRow}`}>
          <label className={styles.formLabel}>
            Payment Date <span className={styles.req}>*</span>
          </label>
          <input
            type="date"
            className={`${styles.formInput} ${fieldErrors.date ? styles.formInputError : ""}`}
            value={form.date}
            onChange={(e) => set("date", e.target.value)}
          />
          {fieldErrors.date && (
            <div className={styles.fieldError}>{fieldErrors.date}</div>
          )}
        </div>
        <div className={`${styles.formGroup} ${styles.formGroupInRow}`}>
          <label className={styles.formLabel}>Currency</label>
          <select
            className={styles.formSelect}
            value={form.currency}
            onChange={(e) => set("currency", e.target.value)}
          >
            <option value="EUR">EUR - Euro</option>
            <option value="USD">USD - US Dollar</option>
          </select>
        </div>
      </div>

      {/* Account */}
      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Account {form.method && <span className={styles.req}>*</span>}</label>
        <select
          className={`${styles.formSelect} ${fieldErrors.accountId ? styles.formInputError : ""}`}
          value={form.accountId}
          onChange={(e) => set("accountId", e.target.value)}
        >
          <option value="">{form.method ? `Select ${form.method} account...` : "Select payment method first..."}</option>
          {accounts.map((acc) => (
            <option key={acc.value} value={acc.value}>
              {acc.label}
            </option>
          ))}
        </select>
        {fieldErrors.accountId && (
          <div className={styles.fieldError}>{fieldErrors.accountId}</div>
        )}
        {form.method && accounts.length === 0 && (
          <span className={styles.formHint} style={{ color: "#b06e0a" }}>No accounts linked to {form.method}. Add one in Finance → Accounts.</span>
        )}
        {!form.method && <span className={styles.formHint}>Select a payment method to see available accounts</span>}
      </div>

      {/* Account Number — shown for bank-type methods, read-only when auto-filled */}
      {showAccountNumber && (
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>
            Account Number / IBAN <span className={styles.req}>*</span>
          </label>
          <input
            type="text"
            className={`${styles.formInput} ${fieldErrors.accountNumber ? styles.formInputError : ""} ${accountHasNumber ? styles.formInputReadonly : ""}`}
            placeholder={accountHasNumber ? "" : "e.g., BE68 5390 0754 7034"}
            value={form.accountNumber}
            onChange={(e) => { if (!accountHasNumber) set("accountNumber", e.target.value); }}
            readOnly={accountHasNumber}
          />
          {fieldErrors.accountNumber && (
            <div className={styles.fieldError}>{fieldErrors.accountNumber}</div>
          )}
          <span className={styles.formHint}>{accountHasNumber ? "Auto-filled from the selected account" : "Enter the bank account number or IBAN"}</span>
        </div>
      )}

      {/* Transaction fee & bearer — SUNSKY supplier fee logic (outgoing only) */}
      {isOutgoingType && (
        <div className={styles.formRow2}>
          <div className={`${styles.formGroup} ${styles.formGroupInRow}`}>
            <label className={styles.formLabel}>Transaction Cost</label>
            <input
              type="text"
              className={styles.formInput}
              placeholder="0,00"
              value={form.transactionFee}
              onChange={(e) => set("transactionFee", e.target.value)}
            />
            <span className={styles.formHint}>Provider fee for this outgoing payment (optional).</span>
          </div>
          <div className={`${styles.formGroup} ${styles.formGroupInRow}`}>
            <label className={styles.formLabel}>Cost Paid By {feeNum > 0 && <span className={styles.req}>*</span>}</label>
            <select
              className={styles.formSelect}
              value={form.feePaidBy}
              onChange={(e) => set("feePaidBy", e.target.value)}
            >
              <option value="sunsky">Sunsky — we pay the cost (recommended)</option>
              <option value="supplier">Supplier — cost deducted from their amount</option>
            </select>
            <span className={styles.formHint}>
              {form.feePaidBy === "supplier"
                ? "Only when the contract states the supplier bears the payment cost."
                : "Cost is a Sunsky bank expense — the supplier receives the full amount."}
            </span>
          </div>
        </div>
      )}

      {/* Notes */}
      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Notes</label>
        <textarea
          className={styles.formTextarea}
          rows={2}
          maxLength={255}
          placeholder="Payment for selected products."
          value={form.notes}
          onChange={(e) => set("notes", e.target.value)}
        />
        <span className={styles.formCharCount}>
          {(form.notes || "").length} / 255
        </span>
      </div>

      {/* Attachments */}
      <div className={styles.attachments}>
        <h4 className={styles.attachmentsTitle}>
          <Upload size={14} /> Attachments
          {attachments.length > 0 && (
            <span className={styles.attachmentCount}>{attachments.length}</span>
          )}
          <span className={styles.attachmentsOptional}>(optional)</span>
        </h4>

        {/* Attached files list */}
        {attachments.length > 0 && (
          <div className={styles.attachmentList}>
            {attachments.map((a) => (
              <div key={a.id} className={styles.attachmentItem}>
                <div className={styles.attachmentIcon}>
                  <FileText size={16} />
                </div>
                <div className={styles.attachmentInfo}>
                  <span className={styles.attachmentName}>{a.name}</span>
                  <span className={styles.attachmentSize}>{formatFileSize(a.size)}</span>
                </div>
                <button
                  type="button"
                  className={styles.attachmentRemove}
                  onClick={() => removeAttachment(a.id)}
                  title="Remove attachment"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Upload area with drag & drop */}
        <div
          className={styles.uploadArea}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.currentTarget.classList.add(styles.uploadAreaDragOver);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.currentTarget.classList.remove(styles.uploadAreaDragOver);
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.currentTarget.classList.remove(styles.uploadAreaDragOver);
            const dt = e.dataTransfer;
            if (dt?.files?.length) {
              handleFileSelect({ target: { files: dt.files } });
            }
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.png,.jpg,.jpeg"
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />
          <div className={styles.uploadIcon}>
            <Upload size={18} />
          </div>
          <div className={styles.uploadText}>
            <span className={styles.uploadTextLink}>Click to upload</span>{" "}
            or drag and drop files here
          </div>
          <div className={styles.uploadHint}>
            PDF, PNG, JPG — max 10MB per file
          </div>
        </div>
      </div>
    </div>
  );

  /* ===================================================================
     STEP 2: Select Products & Travelers
     =================================================================== */

  // ── Payment Allocation UI (hotels) ──────────────────────────────────────────

  const renderAllocationToggle = (product) => {
    if (!hasRoomAllocation(product)) return null;
    const mode = getAllocMode(product);
    return (
      <div className={styles.allocBar}>
        <div className={styles.allocBarLeft}>
          <span className={styles.allocBarLabel}>Payment Allocation</span>
          <span className={styles.allocBarHint}>
            {mode === "per_room"
              ? "The payment is recorded against the complete room amounts. Travelers are shown for information only."
              : "The payment is allocated to the individual travelers."}
          </span>
        </div>
        <div className={styles.allocBtns}>
          <button
            type="button"
            className={`${styles.allocBtn} ${mode === "per_room" ? styles.allocBtnActive : ""}`}
            onClick={() => switchAllocMode(product, "per_room")}
          >
            <BedDouble size={13} /> Per Room
            <span className={styles.allocDefaultTag}>Default</span>
          </button>
          <button
            type="button"
            className={`${styles.allocBtn} ${mode === "per_traveler" ? styles.allocBtnActive : ""}`}
            onClick={() => switchAllocMode(product, "per_traveler")}
            disabled={getTravelersForProduct(product.id).length === 0}
            title={
              getTravelersForProduct(product.id).length === 0
                ? "No travelers assigned to this hotel"
                : undefined
            }
          >
            <Users size={13} /> Per Traveler
          </button>
        </div>
      </div>
    );
  };

  const renderRoomRows = (product) => {
    const rooms = product.rooms || [];
    const travelers = getTravelersForProduct(product.id);
    const payTotal = getProductPayAmount(product);
    return (
      <div className={styles.travelerSection}>
        <div className={styles.roomTableHead}>
          <span>Room</span>
          <span>Total Cost</span>
          <span>Already Paid</span>
          <span>Outstanding</span>
          <span>Payment Amount</span>
        </div>
        {rooms.map((r, idx) => {
          const key = `${product.id}-room-${r.id}`;
          const enteredAmt = parseFloat(roomAmounts[key] || 0);
          const alreadyPaid = Math.round((paidByAllocation.rooms[key] || 0) * 100) / 100;
          const outstanding = Math.round((r.cost - alreadyPaid - enteredAmt) * 100) / 100;
          const maxPayable = Math.max(0, Math.round((r.cost - alreadyPaid) * 100) / 100);
          return (
            <div key={key} className={styles.roomRow}>
              <div className={styles.roomInfo}>
                <div className={styles.roomIcon}>
                  <BedDouble size={15} />
                </div>
                <div>
                  <div className={styles.roomName}>
                    Room {idx + 1}{r.label ? ` — ${r.label}` : ""}
                  </div>
                  {r.board && <span className={styles.roomBoard}>{r.board}</span>}
                </div>
              </div>
              <span className={styles.travelerCost}>{fmt(r.cost)}</span>
              <span className={styles.travelerPaid}>{fmt(alreadyPaid)}</span>
              <span className={`${styles.travelerOutstanding} ${getOutstandingClass(r.cost, alreadyPaid + enteredAmt)}`}>
                {fmt(outstanding)}
              </span>
              <div className={styles.travelerPayInput}>
                <span className={styles.payPrefix}>&euro;</span>
                <input
                  type="text"
                  value={roomAmounts[key] || ""}
                  placeholder="0,00"
                  style={amountErrors[key] ? { borderColor: "#ef4444" } : undefined}
                  onChange={(e) => handleRoomAmountChange(key, e.target.value, maxPayable)}
                />
              </div>
            </div>
          );
        })}
        <div className={styles.productTotal}>
          <span style={{ fontSize: "11px", color: "#94a3b8" }}>
            {rooms.length} room{rooms.length !== 1 ? "s" : ""} — payment per complete room amount
          </span>
          <div>
            <span className={styles.productTotalLabel}>Product Total: </span>
            <span
              className={`${styles.productTotalValue} ${payTotal > 0 && Math.abs(payTotal - product.due) < 0.01 ? styles.productTotalMatch : ""}`}
            >
              {fmt(payTotal)}
            </span>
          </div>
        </div>
        {travelers.length > 0 && (
          <div className={styles.roomTravelersInfo}>
            <div className={styles.roomTravelersTitle}>
              <Users size={12} /> Travelers in this hotel — shown for information only, no
              amount is allocated to individual travelers
            </div>
            <div className={styles.roomTravelerChips}>
              {travelers.map((t) => (
                <span key={t.id} className={styles.roomTravelerChip}>
                  <span className={`${styles.avatar} ${getAvatarClass(t.type)}`}>
                    {renderInitials(t.name)}
                  </span>
                  {t.name}
                  <span className={`${styles.travelerTypeBadge} ${getTravelerBadgeClass(t.type)}`}>
                    {t.type || "Adult"}
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTravelerRows = (product) => {
    const travelers = getTravelersForProduct(product.id);
    if (travelers.length === 0) return null;

    const segs = product.flightSegments;
    const hasSegments =
      product.type === "Flight" && segs && (segs.outbound || segs.inbound);

    const renderSegmentTravelers = (seg, prefix, dirLabel, dirColor) => {
      if (!seg) return null;
      // Each direction shows the cost actually priced for that leg per traveler.
      // Only when a leg carries no per-traveler pricing does it fall back to an
      // equal share of the (halved, for a two-leg split) product cost.
      const legFallback =
        (product.maxAmount || 0) /
        (hasSegments && segs.outbound && segs.inbound ? 2 : 1);
      const costInfo = getTravelerCostInfo(product, travelers, prefix, legFallback);
      const segTotal = costInfo.total;

      const segSum = Math.round(travelers.reduce(
        (s, t) =>
          s +
          (parseFloat(travelerAmounts[`${product.id}-${prefix}-${t.id}`] || 0) ||
            0),
        0
      ) * 100) / 100;

      return (
        <div key={dirLabel} className={styles.segmentBlock}>
          <div
            className={styles.segmentHeader}
            style={{
              background:
                dirLabel === "Outbound" ? "#eef9f3" : "#eef3ff",
              borderBottom: `1px solid ${dirLabel === "Outbound" ? "#c3e8d4" : "#c7d8f7"}`,
            }}
          >
            <div className={styles.segmentHeaderLeft}>
              <Plane
                size={13}
                style={{
                  color: dirColor,
                  transform:
                    dirLabel === "Inbound" ? "scaleX(-1)" : "none",
                }}
              />
              <div>
                <div className={styles.segmentDir} style={{ color: dirColor }}>
                  {dirLabel}
                </div>
                <div className={styles.segmentRoute}>{seg.route}</div>
              </div>
            </div>
            <div className={styles.segmentHeaderRight}>
              {seg.airline && (
                <span>{seg.airlineCode || seg.airline}</span>
              )}
              <span className={styles.segmentPnr}>
                PNR: {seg.pnr || "—"}
              </span>
            </div>
          </div>
          <div className={styles.travelerTableHead}>
            <span>Traveler</span>
            <span>Ticket Number</span>
            <span>Total Cost</span>
            <span>Already Paid</span>
            <span>Outstanding</span>
            <span>Payment Amount</span>
          </div>
          {travelers.map((t) => {
            const key = `${product.id}-${prefix}-${t.id}`;
            const enteredAmt = parseFloat(travelerAmounts[key] || 0);
            const alreadyPaid = Math.round((paidByAllocation.travelers[key] || 0) * 100) / 100;
            const travelerCost = costInfo.costOf(t);
            const travelerOutstanding = Math.round((travelerCost - alreadyPaid - enteredAmt) * 100) / 100;
            return (
              <div key={key} className={styles.travelerRow}>
                <div className={styles.travelerInfo}>
                  <div
                    className={`${styles.avatar} ${getAvatarClass(t.type)}`}
                  >
                    {renderInitials(t.name)}
                  </div>
                  <div>
                    <div className={styles.travelerName}>{t.name}</div>
                    <span
                      className={`${styles.travelerTypeBadge} ${getTravelerBadgeClass(t.type)}`}
                    >
                      {t.type || "Adult"}
                    </span>
                  </div>
                </div>
                <span className={styles.travelerTicket}>
                  {t.ticketNumber || "—"}
                </span>
                <span className={styles.travelerCost}>
                  {fmt(travelerCost)}
                </span>
                <span className={styles.travelerPaid}>{fmt(alreadyPaid)}</span>
                <span className={`${styles.travelerOutstanding} ${getOutstandingClass(travelerCost, alreadyPaid + enteredAmt)}`}>
                  {fmt(travelerOutstanding)}
                </span>
                <div className={styles.travelerPayInput}>
                  <span className={styles.payPrefix}>&euro;</span>
                  <input
                    type="text"
                    value={travelerAmounts[key] || ""}
                    placeholder="0,00"
                    className={amountErrors[key] ? "inputError" : ""}
                    style={
                      amountErrors[key]
                        ? { borderColor: "#ef4444" }
                        : undefined
                    }
                    onChange={(e) =>
                      handleTravelerAmountChange(
                        key,
                        e.target.value,
                        travelerCost
                      )
                    }
                  />
                </div>
              </div>
            );
          })}
          <div className={styles.segmentFooter}>
            <div className={styles.segmentFooterLeft}>
              {costInfo.exact ? (
                // Every traveler on this leg has their own purchase price — offer
                // to fill exactly that, never an equal division.
                <button
                  type="button"
                  className={styles.splitBtn}
                  onClick={() => fillExactCosts(product, travelers, prefix, costInfo)}
                >
                  <Wallet size={12} /> Fill actual cost per traveler
                </button>
              ) : (
                <button
                  type="button"
                  className={styles.splitBtn}
                  onClick={() =>
                    splitEqually(product.id, prefix, travelers, costInfo.perPerson)
                  }
                >
                  <Split size={12} /> Split equally — {fmt(costInfo.perPerson)} / person
                </button>
              )}
            </div>
            <div className={styles.segmentFooterRight}>
              <span className={styles.segmentTotalLabel}>
                {dirLabel} Total
              </span>
              <span
                className={`${styles.segmentTotalValue} ${segSum > 0 && Math.abs(segSum - segTotal) < 0.01 ? styles.productTotalMatch : ""}`}
              >
                {fmt(segSum)}
                {segSum > 0 &&
                  Math.abs(segSum - segTotal) < 0.01 && (
                    <Check
                      size={13}
                      style={{ marginLeft: 4, verticalAlign: "middle" }}
                    />
                  )}
              </span>
            </div>
          </div>
        </div>
      );
    };

    if (hasSegments) {
      const outSum = segs.outbound ? travelers.reduce(
        (s, t) => s + (parseFloat(travelerAmounts[`${product.id}-out-${t.id}`] || 0) || 0), 0
      ) : 0;
      const inSum = segs.inbound ? travelers.reduce(
        (s, t) => s + (parseFloat(travelerAmounts[`${product.id}-in-${t.id}`] || 0) || 0), 0
      ) : 0;
      const grandSum = outSum + inSum;

      return (
        <div className={styles.travelerSection}>
          {renderSegmentTravelers(
            segs.outbound,
            "out",
            "Outbound",
            "#0d7a4f"
          )}
          {renderSegmentTravelers(
            segs.inbound,
            "in",
            "Inbound",
            "#2563eb"
          )}
          {/* Grand total for split flight */}
          {segs.outbound && segs.inbound && (
            <div className={styles.splitGrandTotal}>
              <div className={styles.splitGrandTotalBreakdown}>
                <span className={styles.splitGrandTotalItem}>
                  <Plane size={12} style={{ color: "#0d7a4f" }} /> Outbound: <strong>{fmt(outSum)}</strong>
                </span>
                <span className={styles.splitGrandTotalPlus}>+</span>
                <span className={styles.splitGrandTotalItem}>
                  <Plane size={12} style={{ color: "#2563eb", transform: "scaleX(-1)" }} /> Inbound: <strong>{fmt(inSum)}</strong>
                </span>
              </div>
              <div className={styles.splitGrandTotalRight}>
                <span className={styles.splitGrandTotalLabel}>Grand Total</span>
                <span className={`${styles.splitGrandTotalValue} ${grandSum > 0 && Math.abs(grandSum - product.due) < 0.01 ? styles.productTotalMatch : ""}`}>
                  {fmt(grandSum)}
                  {grandSum > 0 && Math.abs(grandSum - product.due) < 0.01 && (
                    <Check size={13} style={{ marginLeft: 4, verticalAlign: "middle" }} />
                  )}
                </span>
              </div>
            </div>
          )}
        </div>
      );
    }

    // Non-segmented travelers (hotel, transfer, single flight). Each traveler
    // shows the purchase price entered for them; an equal share is used only when
    // the product has no per-traveler pricing to read back.
    const costInfo = getTravelerCostInfo(product, travelers);

    return (
      <div className={styles.travelerSection}>
        <div className={styles.travelerTableHead}>
          <span>Traveler</span>
          <span>Ticket Number</span>
          <span>Total Cost</span>
          <span>Already Paid</span>
          <span>Outstanding</span>
          <span>Payment Amount</span>
        </div>
        {travelers.map((t) => {
          const key = `${product.id}-${t.id}`;
          const enteredAmt = parseFloat(travelerAmounts[key] || 0);
          const alreadyPaid = Math.round((paidByAllocation.travelers[key] || 0) * 100) / 100;
          const travelerCost = costInfo.costOf(t);
          const travelerOutstanding = Math.round((travelerCost - alreadyPaid - enteredAmt) * 100) / 100;
          return (
            <div key={key} className={styles.travelerRow}>
              <div className={styles.travelerInfo}>
                <div
                  className={`${styles.avatar} ${getAvatarClass(t.type)}`}
                >
                  {renderInitials(t.name)}
                </div>
                <div>
                  <div className={styles.travelerName}>{t.name}</div>
                  <span
                    className={`${styles.travelerTypeBadge} ${getTravelerBadgeClass(t.type)}`}
                  >
                    {t.type || "Adult"}
                  </span>
                </div>
              </div>
              <span className={styles.travelerTicket}>
                {t.ticketNumber || "—"}
              </span>
              <span className={styles.travelerCost}>{fmt(travelerCost)}</span>
              <span className={styles.travelerPaid}>{fmt(alreadyPaid)}</span>
              <span className={`${styles.travelerOutstanding} ${getOutstandingClass(travelerCost, alreadyPaid + enteredAmt)}`}>
                {fmt(travelerOutstanding)}
              </span>
              <div className={styles.travelerPayInput}>
                <span className={styles.payPrefix}>&euro;</span>
                <input
                  type="text"
                  value={travelerAmounts[key] || ""}
                  placeholder="0,00"
                  style={
                    amountErrors[key]
                      ? { borderColor: "#ef4444" }
                      : undefined
                  }
                  onChange={(e) =>
                    handleTravelerAmountChange(key, e.target.value, travelerCost)
                  }
                />
              </div>
            </div>
          );
        })}
        <div className={styles.productTotal}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {costInfo.exact ? (
              <>
                <button
                  type="button"
                  className={styles.splitBtn}
                  onClick={() => fillExactCosts(product, travelers, null, costInfo)}
                >
                  <Wallet size={12} /> Fill actual cost per traveler
                </button>
                <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                  Cost shown per traveler as priced in this booking
                </span>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className={styles.splitBtn}
                  onClick={() =>
                    splitEqually(product.id, null, travelers, costInfo.perPerson)
                  }
                >
                  <Split size={12} /> Split equally
                </button>
                <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                  {fmt(costInfo.perPerson)} / person — no individual cost recorded
                </span>
              </>
            )}
          </div>
          <div>
            <span className={styles.productTotalLabel}>Product Total: </span>
            <span
              className={`${styles.productTotalValue} ${getProductPayAmount(product) > 0 && Math.abs(getProductPayAmount(product) - product.due) < 0.01 ? styles.productTotalMatch : ""}`}
            >
              {fmt(getProductPayAmount(product))}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderStep2 = () => {
    if (!activeSupplier) {
      return (
        <div className={styles.stepContent} key="step2">
          <div className={styles.warningBanner}>
            <AlertCircle size={14} />
            <span>No supplier selected. Please go back and select a supplier first.</span>
          </div>
        </div>
      );
    }

    const currentViewId = viewingProductId || (products.length > 0 ? products[0].id : null);
    const viewingProd = products.find((p) => p.id === currentViewId);
    const allTravelerCount = products.reduce((s, p) => s + getTravelersForProduct(p.id).length, 0);

    return (
      <div className={styles.stepContent} key="step2">
        {/* ── Stats bar ── */}
        <div className={styles.s2StatsBar}>
          <div className={styles.s2SupplierBox}>
            <span className={styles.s2SupplierLabel}>Supplier</span>
            <span className={styles.s2SupplierName}>{activeSupplier?.name || "—"}</span>
          </div>
          <div className={styles.s2StatCard}>
            <span className={styles.s2StatLabel}>Total Due</span>
            <span className={styles.s2StatValue}>{fmt(totalDue)}</span>
          </div>
          <div className={styles.s2StatCard}>
            <span className={styles.s2StatLabel}>Already Paid</span>
            <span className={`${styles.s2StatValue} ${styles.s2StatGreen}`}>{fmt(supplierAlreadyPaid)}</span>
          </div>
          <div className={styles.s2StatCard}>
            <span className={styles.s2StatLabel}>Outstanding</span>
            <span className={`${styles.s2StatValue} ${getStatOutstandingClass(totalDue, supplierAlreadyPaid + (parseFloat(form.amount) || 0))}`}>{fmt(totalDue - supplierAlreadyPaid - (parseFloat(form.amount) || 0))}</span>
          </div>
          <div className={styles.s2StatCard}>
            <span className={styles.s2StatLabel}>Scope</span>
            <span className={styles.s2StatValue}>{products.length}P / {allTravelerCount}T</span>
          </div>
          <div className={styles.s2InfoTip}>
            <Info size={14} />
            <span>Select a product and enter the payment per room or per traveler. Only one product can be paid at a time.</span>
          </div>
        </div>

        {/* Reference / Transaction ID — product-specific */}
        <div className={styles.formGroup} style={{ marginBottom: 12 }}>
          <label className={styles.formLabel}>
            Reference / Transaction ID
            <Info size={13} style={{ marginLeft: 4, verticalAlign: "middle", opacity: 0.5 }} />
          </label>
          <input
            type="text"
            className={styles.formInput}
            placeholder={selectedProducts.length > 0 ? "Auto-populated from selected product" : "Select a product below to auto-populate"}
            value={form.reference}
            onChange={(e) => set("reference", e.target.value)}
          />
          <span className={styles.formHint}>
            {selectedProducts.length > 0
              ? "Auto-populated from selected product. You can edit manually."
              : "Select a product below to auto-populate the supplier reference."}
          </span>
        </div>

        {/* ── Two-column layout ── */}
        <div className={styles.s2Layout}>
          {/* ── Left: Products sidebar ── */}
          <div className={styles.s2Sidebar}>
            <div className={styles.s2SidebarHeader}>
              <span>Products in this booking</span>
              <span className={styles.s2CountBadge}>{products.length}</span>
            </div>

            <div className={styles.s2ProductList}>
              {products.map((p) => {
                const isViewing = p.id === currentViewId;
                const isIncluded = !!selected[p.id];
                const travelers = getTravelersForProduct(p.id);
                const segs = p.flightSegments;
                const isSplitNav = p.type === "Flight" && segs && segs.outbound && segs.inbound;

                if (isSplitNav) {
                  return (
                    <div
                      key={p.id}
                      className={`${styles.splitNavCard} ${isViewing ? styles.splitNavCardActive : ""} ${isIncluded ? styles.splitNavCardIncluded : ""}`}
                      onClick={() => {
                        setViewingProductId(p.id);
                        if (!expanded[p.id]) setExpanded((e) => ({ ...e, [p.id]: true }));
                      }}
                    >
                      <div className={styles.splitNavTop}>
                        <input
                          type="radio"
                          name="selectedProduct"
                          className={styles.s2NavRadio}
                          checked={isIncluded}
                          onChange={(e) => { e.stopPropagation(); toggleProduct(p.id); }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className={`${styles.badge} ${styles.badgeSplit}`}><Split size={10} /> Split Flight</span>
                        <span className={styles.splitNavTravelers}><Users size={11} /> {travelers.length}</span>
                        <span className={`${styles.splitNavDue} ${isIncluded ? styles.s2DueGreen : ""}`}>{fmt(p.due)}</span>
                      </div>
                      <div className={styles.splitNavLegs}>
                        <div className={styles.splitNavLeg}>
                          <div className={styles.splitNavLegDot} style={{ background: "#0d7a4f" }} />
                          <div className={styles.splitNavLegBody}>
                            <div className={styles.splitNavLegTop}>
                              <span className={styles.splitNavLegDir} style={{ color: "#0d7a4f" }}>Outbound</span>
                              <span className={styles.splitNavLegRoute}>{segs.outbound.route}</span>
                            </div>
                            <div className={styles.splitNavLegBottom}>
                              {segs.outbound.airlineCode && <span className={styles.splitNavLegTag}>{segs.outbound.airlineCode}</span>}
                              {segs.outbound.date && <span className={styles.splitNavLegDate}><Calendar size={9} /> {segs.outbound.date}</span>}
                              <span className={styles.splitNavLegPnr}>PNR: {segs.outbound.pnr || "—"}</span>
                            </div>
                          </div>
                        </div>
                        <div className={styles.splitNavLeg}>
                          <div className={styles.splitNavLegDot} style={{ background: "#2563eb" }} />
                          <div className={styles.splitNavLegBody}>
                            <div className={styles.splitNavLegTop}>
                              <span className={styles.splitNavLegDir} style={{ color: "#2563eb" }}>Inbound</span>
                              <span className={styles.splitNavLegRoute}>{segs.inbound.route}</span>
                            </div>
                            <div className={styles.splitNavLegBottom}>
                              {segs.inbound.airlineCode && <span className={styles.splitNavLegTag}>{segs.inbound.airlineCode}</span>}
                              {segs.inbound.date && <span className={styles.splitNavLegDate}><Calendar size={9} /> {segs.inbound.date}</span>}
                              <span className={styles.splitNavLegPnr}>PNR: {segs.inbound.pnr || "—"}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }

                const hasSegs = p.type === "Flight" && segs && (segs.outbound || segs.inbound);
                const singleSeg = hasSegs ? (segs.outbound || segs.inbound) : null;

                return (
                  <div
                    key={p.id}
                    className={`${styles.prodCard} ${isViewing ? styles.prodCardActive : ""} ${isIncluded ? styles.prodCardIncluded : ""}`}
                    onClick={() => {
                      setViewingProductId(p.id);
                      if (!expanded[p.id]) setExpanded((e) => ({ ...e, [p.id]: true }));
                    }}
                  >
                    <div className={styles.prodCardTop}>
                      <input
                        type="radio"
                        name="selectedProduct"
                        className={styles.s2NavRadio}
                        checked={isIncluded}
                        onChange={(e) => { e.stopPropagation(); toggleProduct(p.id); }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className={`${styles.productIcon} ${getProductIconClass(p.type)}`}>
                        {getProductTypeIcon(p.type)}
                      </div>
                      <span className={`${styles.badge} ${getBadgeClass(p.type)}`}>{p.type.toUpperCase()}</span>
                      <span className={styles.prodCardTravelers}><Users size={11} /> {travelers.length}</span>
                      <span className={`${styles.prodCardDue} ${isIncluded ? styles.s2DueGreen : ""}`}>{fmt(p.due)}</span>
                    </div>
                    <div className={styles.prodCardBody}>
                      <div className={styles.prodCardName}>{p.name}</div>
                      <div className={styles.prodCardMeta}>
                        {p.pnr && p.pnr !== "—" && (
                          <span className={styles.prodCardPnr}>{p.pnrLabel}: {p.pnr}</span>
                        )}
                        {hasSegs && singleSeg?.airlineCode && (
                          <span className={styles.prodCardTag}>{singleSeg.airlineCode}</span>
                        )}
                        {p.dateInfo && (
                          <span className={styles.prodCardDate}><Calendar size={10} /> {p.dateInfo}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={styles.s2SidebarTotal}>
              <span>Total Outstanding</span>
              <span className={styles.s2SidebarTotalVal} style={{ color: supplierAlreadyPaid + (parseFloat(form.amount) || 0) >= totalDue ? "#16a34a" : supplierAlreadyPaid + (parseFloat(form.amount) || 0) > 0 ? "#f59e0b" : undefined }}>{fmt(totalDue - supplierAlreadyPaid - (parseFloat(form.amount) || 0))}</span>
            </div>

            <div className={styles.s2Tip}>
              <Info size={14} />
              <span>Tip: You can expand a product to view traveler breakdown and enter payment per traveler.</span>
            </div>
          </div>

          {/* ── Right: Detail panel ── */}
          <div className={styles.s2Detail}>
            {viewingProd && (
              <>
                {/* Detail header for viewed product */}
                {viewingProd.type === "Flight" && viewingProd.flightSegments && viewingProd.flightSegments.outbound && viewingProd.flightSegments.inbound ? (
                  <div className={styles.splitDetailHeader}>
                    <div className={styles.splitDetailTitleRow}>
                      <div className={`${styles.productIcon} ${styles.productIconFlight}`}>
                        <Plane size={16} />
                      </div>
                      <span className={`${styles.badge} ${styles.badgeSplit}`}>
                        <Split size={11} /> SPLIT FLIGHT
                      </span>
                      <span className={styles.splitDetailName}>{viewingProd.name}</span>
                      <span className={styles.s2DetailMetaItem}>
                        <Users size={12} /> {getTravelersForProduct(viewingProd.id).length} Travelers
                      </span>
                    </div>
                    <div className={styles.splitDetailSegments}>
                      <div className={styles.splitDetailSeg} style={{ borderLeft: "3px solid #0d7a4f", background: "#f0fdf8" }}>
                        <div className={styles.splitDetailSegTop}>
                          <Plane size={13} style={{ color: "#0d7a4f" }} />
                          <span className={styles.splitDetailSegDir} style={{ color: "#0d7a4f" }}>Outbound</span>
                          <span className={styles.splitDetailSegRoute}>{viewingProd.flightSegments.outbound.route}</span>
                        </div>
                        <div className={styles.splitDetailSegBottom}>
                          {viewingProd.flightSegments.outbound.airlineCode && (
                            <span className={styles.splitDetailSegTag}>{viewingProd.flightSegments.outbound.airlineCode}</span>
                          )}
                          {viewingProd.flightSegments.outbound.date && (
                            <span className={styles.splitDetailSegMeta}><Calendar size={10} /> {viewingProd.flightSegments.outbound.date}</span>
                          )}
                          <span className={styles.splitDetailSegPnr}>PNR: <strong>{viewingProd.flightSegments.outbound.pnr || "—"}</strong></span>
                        </div>
                      </div>
                      <div className={styles.splitDetailSegDivider}>
                        <ArrowRight size={14} />
                      </div>
                      <div className={styles.splitDetailSeg} style={{ borderLeft: "3px solid #2563eb", background: "#eff4ff" }}>
                        <div className={styles.splitDetailSegTop}>
                          <Plane size={13} style={{ color: "#2563eb", transform: "scaleX(-1)" }} />
                          <span className={styles.splitDetailSegDir} style={{ color: "#2563eb" }}>Inbound</span>
                          <span className={styles.splitDetailSegRoute}>{viewingProd.flightSegments.inbound.route}</span>
                        </div>
                        <div className={styles.splitDetailSegBottom}>
                          {viewingProd.flightSegments.inbound.airlineCode && (
                            <span className={styles.splitDetailSegTag}>{viewingProd.flightSegments.inbound.airlineCode}</span>
                          )}
                          {viewingProd.flightSegments.inbound.date && (
                            <span className={styles.splitDetailSegMeta}><Calendar size={10} /> {viewingProd.flightSegments.inbound.date}</span>
                          )}
                          <span className={styles.splitDetailSegPnr}>PNR: <strong>{viewingProd.flightSegments.inbound.pnr || "—"}</strong></span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={styles.s2DetailHeader}>
                    <div className={`${styles.productIcon} ${getProductIconClass(viewingProd.type)}`}>
                      {getProductTypeIcon(viewingProd.type)}
                    </div>
                    <span className={`${styles.badge} ${getBadgeClass(viewingProd.type)}`}>{viewingProd.type.toUpperCase()}</span>
                    <span className={styles.s2DetailRoute}>{viewingProd.name}</span>
                    <div className={styles.s2DetailMeta}>
                      {viewingProd.dateInfo && (
                        <span className={styles.s2DetailMetaItem}>
                          <Calendar size={12} /> {viewingProd.dateInfo}
                        </span>
                      )}
                      <span className={styles.s2DetailMetaItem}>
                        <Users size={12} /> Travelers: {getTravelersForProduct(viewingProd.id).length}
                      </span>
                      <span className={styles.s2DetailMetaItem}>
                        {viewingProd.pnrLabel}{" "}
                        <span className={styles.s2PnrCode}>{viewingProd.pnr}</span>
                      </span>
                    </div>
                  </div>
                )}

                {/* Info banner */}
                <div className={styles.s2DetailBanner}>
                  <Info size={14} />
                  <span>
                    {selected[viewingProd.id] && getAllocMode(viewingProd) === "per_room"
                      ? "Enter payment per room. Amounts are in EUR."
                      : "Enter payment per traveler. Amounts are in EUR."}
                  </span>
                </div>

                {/* Payment Allocation (Per Room ⇄ Per Traveler) — hotels only */}
                {selected[viewingProd.id] && renderAllocationToggle(viewingProd)}

                {/* Room or traveler rows for viewed product */}
                {selected[viewingProd.id] &&
                  (getAllocMode(viewingProd) === "per_room"
                    ? renderRoomRows(viewingProd)
                    : renderTravelerRows(viewingProd))}

                {/* If no travelers (and no room table), show product cost */}
                {getTravelersForProduct(viewingProd.id).length === 0 &&
                  selected[viewingProd.id] &&
                  getAllocMode(viewingProd) !== "per_room" && (
                  <div className={styles.productTotal}>
                    <span className={styles.productTotalLabel}>Product Cost</span>
                    <span className={styles.productTotalValue}>{fmt(viewingProd.due)}</span>
                  </div>
                )}

                {/* Selected to Pay summary with Pay Amount field */}
                <div className={styles.s2SelectedBar}>
                  <div className={styles.s2SelectedBarLeft}>
                    <span className={styles.s2SelectedBarTitle}>Selected to Pay</span>
                    <span className={styles.s2SelectedBarInfo}>
                      {selectedProducts.length > 0 ? selectedProducts[0].name : "No product selected"}
                      {selectedProducts.length > 0 && getAllocMode(selectedProducts[0]) === "per_room"
                        ? ` · ${(selectedProducts[0].rooms || []).length} room${(selectedProducts[0].rooms || []).length !== 1 ? "s" : ""} (Per Room)`
                        : ` · ${totalTravelers} traveler${totalTravelers !== 1 ? "s" : ""}`}
                    </span>
                  </div>
                  <div className={styles.s2SelectedBarCenter}>
                    <label className={styles.s2PayAmountLabel}>
                      Pay Amount <span className={styles.req}>*</span>
                    </label>
                    <div className={styles.s2PayAmountWrap}>
                      <span className={styles.s2PayAmountPrefix}>€</span>
                      <input
                        type="text"
                        className={`${styles.s2PayAmountInput} ${fieldErrors.amount ? styles.formInputError : ""}`}
                        placeholder="0,00"
                        value={form.amount}
                        onChange={(e) => set("amount", e.target.value)}
                      />
                    </div>
                    {fieldErrors.amount && (
                      <div className={styles.fieldError} style={{ fontSize: 11 }}>{fieldErrors.amount}</div>
                    )}
                    <span className={styles.s2PayAmountHint}>
                      Auto-filled from selected products. You can adjust manually.
                    </span>
                  </div>
                  <div className={styles.s2SelectedBarRight}>
                    <span className={styles.s2SelectedBarLabel}>Product Total</span>
                    <span className={styles.s2SelectedBarValue}>
                      {fmt(totalPayment)}
                    </span>
                  </div>
                </div>
              </>
            )}

            {!viewingProd && (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}><FileText size={24} /></div>
                <p className={styles.emptyStateTitle}>No products available</p>
                <p className={styles.emptyStateDesc}>This supplier has no products in this booking.</p>
              </div>
            )}
          </div>
        </div>

        {fieldErrors.products && (
          <div className={styles.fieldError} style={{ marginTop: 8, textAlign: "center" }}>
            {fieldErrors.products}
          </div>
        )}
      </div>
    );
  };

  /* ===================================================================
     STEP 3: Review & Confirm
     =================================================================== */

  const renderStep3 = () => {
    const selectedAccount = allAccounts.find((a) => a.value === form.accountId);
    const accountLabel = selectedAccount ? `${selectedAccount.label}${form.accountNumber ? ` (${form.accountNumber})` : ""}` : form.accountNumber || "—";

    return (
      <div className={styles.stepContent} key="step3">
        {/* Summary bar */}
        <div className={styles.summaryBar}>
          <div className={styles.summaryCard}>
            <span className={styles.summaryCardLabel}>Supplier</span>
            <span className={styles.summaryCardValue}>
              {activeSupplier?.name || "—"}
            </span>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryCardLabel}>Total Due</span>
            <span className={styles.summaryCardValue}>{fmt(totalDue)}</span>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryCardLabel}>Already Paid</span>
            <span className={styles.summaryCardValue}>{fmt(supplierAlreadyPaid)}</span>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryCardLabel}>Outstanding</span>
            <span
              className={`${styles.summaryCardValue} ${getSummaryOutstandingClass(totalDue, supplierAlreadyPaid + (parseFloat(form.amount) || 0))}`}
            >
              {fmt(totalDue - supplierAlreadyPaid - (parseFloat(form.amount) || 0))}
            </span>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryCardLabel}>Products</span>
            <span className={styles.summaryCardValue}>
              {selectedProducts.length}
            </span>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryCardLabel}>Travelers</span>
            <span className={styles.summaryCardValue}>{totalTravelers}</span>
          </div>
        </div>

        {/* Fee breakdown — SUNSKY supplier fee logic (only when a fee is set) */}
        {isOutgoingType && feeNum > 0 && (
          <div className={styles.summaryBar}>
            <div className={styles.summaryCard}>
              <span className={styles.summaryCardLabel}>Supplier Amount</span>
              <span className={styles.summaryCardValue}>{fmt(wizardAmount)}</span>
            </div>
            <div className={styles.summaryCard}>
              <span className={styles.summaryCardLabel}>Transaction Cost ({form.feePaidBy === "supplier" ? "supplier pays" : "Sunsky pays"})</span>
              <span className={styles.summaryCardValue}>{fmt(feeNum)}</span>
            </div>
            <div className={styles.summaryCard}>
              <span className={styles.summaryCardLabel}>Total Bank Outgoing</span>
              <span className={styles.summaryCardValue}>{fmt(bankOutgoing)}</span>
            </div>
            <div className={styles.summaryCard}>
              <span className={styles.summaryCardLabel}>Supplier Receives</span>
              <span className={styles.summaryCardValue}>{fmt(netToSupplier)}</span>
            </div>
          </div>
        )}

        {/* Main + Sidebar layout */}
        <div className={styles.reviewLayout}>
          {/* Left: Selected Products */}
          <div className={styles.reviewMain}>
            <div className={styles.reviewSection}>
              <h4 className={styles.reviewSectionTitle}>
                Selected Products &amp; Travelers
              </h4>
              {selectedProducts.map((p) => {
                const travelers = getTravelersForProduct(p.id);
                const segs = p.flightSegments;
                const isSplitFlight = p.type === "Flight" && segs && segs.outbound && segs.inbound;

                if (isSplitFlight) {
                  const outSum = travelers.reduce(
                    (s, t) => s + (parseFloat(travelerAmounts[`${p.id}-out-${t.id}`] || 0)), 0
                  );
                  const inSum = travelers.reduce(
                    (s, t) => s + (parseFloat(travelerAmounts[`${p.id}-in-${t.id}`] || 0)), 0
                  );
                  const grandTotal = outSum + inSum;

                  return (
                    <div key={p.id} className={styles.reviewSplitCard}>
                      {/* Split flight master header with route visualization */}
                      <div className={styles.reviewSplitHeader}>
                        <div className={styles.reviewSplitHeaderTop}>
                          <div className={`${styles.productIcon} ${styles.productIconFlight}`}>
                            <Plane size={16} />
                          </div>
                          <span className={`${styles.badge} ${styles.badgeSplit}`}><Split size={11} /> Split Flight</span>
                          <span className={styles.reviewSplitMeta}>
                            <Users size={12} /> {travelers.length} Traveler{travelers.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <div className={styles.reviewSplitRouteViz}>
                          <div className={styles.reviewSplitRouteCard} style={{ borderColor: "#0d7a4f", background: "#f0fdf8" }}>
                            <div className={styles.reviewSplitRouteDir} style={{ color: "#0d7a4f" }}>
                              <Plane size={12} /> Outbound
                            </div>
                            <div className={styles.reviewSplitRouteText}>{segs.outbound.route}</div>
                            <div className={styles.reviewSplitRouteMeta}>
                              {segs.outbound.airlineCode && <span className={styles.reviewSplitRouteTag}>{segs.outbound.airlineCode}</span>}
                              {segs.outbound.date && <span><Calendar size={10} /> {segs.outbound.date}</span>}
                            </div>
                            <div className={styles.reviewSplitRoutePnr}>
                              PNR <strong>{segs.outbound.pnr || "—"}</strong>
                            </div>
                          </div>
                          <div className={styles.reviewSplitRouteArrow}>
                            <Split size={16} />
                          </div>
                          <div className={styles.reviewSplitRouteCard} style={{ borderColor: "#2563eb", background: "#eff4ff" }}>
                            <div className={styles.reviewSplitRouteDir} style={{ color: "#2563eb" }}>
                              <Plane size={12} style={{ transform: "scaleX(-1)" }} /> Inbound
                            </div>
                            <div className={styles.reviewSplitRouteText}>{segs.inbound.route}</div>
                            <div className={styles.reviewSplitRouteMeta}>
                              {segs.inbound.airlineCode && <span className={styles.reviewSplitRouteTag}>{segs.inbound.airlineCode}</span>}
                              {segs.inbound.date && <span><Calendar size={10} /> {segs.inbound.date}</span>}
                            </div>
                            <div className={styles.reviewSplitRoutePnr}>
                              PNR <strong>{segs.inbound.pnr || "—"}</strong>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Outbound segment travelers */}
                      <div className={styles.reviewSplitSegment}>
                        <div className={styles.reviewSplitSegBanner} style={{ background: "#f0fdf8", borderLeft: "4px solid #0d7a4f" }}>
                          <div className={styles.reviewSplitSegBannerLeft}>
                            <Plane size={14} style={{ color: "#0d7a4f" }} />
                            <span className={styles.reviewSplitSegDir} style={{ color: "#0d7a4f" }}>Outbound</span>
                            <span className={styles.reviewSplitSegRoute}>{segs.outbound.route}</span>
                            <span className={styles.reviewSplitSegPnrInline}>PNR: {segs.outbound.pnr || "—"}</span>
                          </div>
                          <div className={styles.reviewSplitSegBannerRight}>
                            <span className={styles.reviewSplitSegAmt}>{fmt(outSum)}</span>
                          </div>
                        </div>
                        {travelers.map((t) => {
                          const travelAmt = parseFloat(travelerAmounts[`${p.id}-out-${t.id}`] || 0);
                          return (
                            <div key={t.id} className={styles.reviewTravelerRow}>
                              <div className={styles.reviewTravelerInfo}>
                                <div className={`${styles.avatar} ${getAvatarClass(t.type)}`}>{renderInitials(t.name)}</div>
                                <div>
                                  <div className={styles.travelerName}>{t.name}</div>
                                  <span className={`${styles.travelerTypeBadge} ${getTravelerBadgeClass(t.type)}`}>{t.type || "Adult"}</span>
                                </div>
                              </div>
                              <span className={styles.reviewTravelerAmount}>{fmt(travelAmt)}</span>
                            </div>
                          );
                        })}
                      </div>

                      {/* Inbound segment travelers */}
                      <div className={styles.reviewSplitSegment}>
                        <div className={styles.reviewSplitSegBanner} style={{ background: "#eff4ff", borderLeft: "4px solid #2563eb" }}>
                          <div className={styles.reviewSplitSegBannerLeft}>
                            <Plane size={14} style={{ color: "#2563eb", transform: "scaleX(-1)" }} />
                            <span className={styles.reviewSplitSegDir} style={{ color: "#2563eb" }}>Inbound</span>
                            <span className={styles.reviewSplitSegRoute}>{segs.inbound.route}</span>
                            <span className={styles.reviewSplitSegPnrInline}>PNR: {segs.inbound.pnr || "—"}</span>
                          </div>
                          <div className={styles.reviewSplitSegBannerRight}>
                            <span className={styles.reviewSplitSegAmt}>{fmt(inSum)}</span>
                          </div>
                        </div>
                        {travelers.map((t) => {
                          const travelAmt = parseFloat(travelerAmounts[`${p.id}-in-${t.id}`] || 0);
                          return (
                            <div key={t.id} className={styles.reviewTravelerRow}>
                              <div className={styles.reviewTravelerInfo}>
                                <div className={`${styles.avatar} ${getAvatarClass(t.type)}`}>{renderInitials(t.name)}</div>
                                <div>
                                  <div className={styles.travelerName}>{t.name}</div>
                                  <span className={`${styles.travelerTypeBadge} ${getTravelerBadgeClass(t.type)}`}>{t.type || "Adult"}</span>
                                </div>
                              </div>
                              <span className={styles.reviewTravelerAmount}>{fmt(travelAmt)}</span>
                            </div>
                          );
                        })}
                      </div>

                      {/* Grand total footer */}
                      <div className={styles.reviewSplitFooter}>
                        <div className={styles.reviewSplitFooterBreakdown}>
                          <span className={styles.reviewSplitFooterItem}>
                            <Plane size={12} style={{ color: "#0d7a4f" }} /> Outbound: <strong>{fmt(outSum)}</strong>
                          </span>
                          <span className={styles.reviewSplitFooterPlus}>+</span>
                          <span className={styles.reviewSplitFooterItem}>
                            <Plane size={12} style={{ color: "#2563eb", transform: "scaleX(-1)" }} /> Inbound: <strong>{fmt(inSum)}</strong>
                          </span>
                        </div>
                        <div className={styles.reviewSplitFooterTotal}>
                          <span className={styles.reviewSplitFooterTotalLabel}>Grand Total</span>
                          <span className={styles.reviewSplitFooterTotalValue}>{fmt(grandTotal)}</span>
                        </div>
                      </div>
                    </div>
                  );
                }

                // Hotel paid Per Room — the payment is divided over complete room
                // amounts; travelers are listed for information only.
                if (getAllocMode(p) === "per_room") {
                  return (
                    <div key={p.id} className={styles.reviewProductCard}>
                      <div className={styles.reviewProductHeader}>
                        <div className={`${styles.productIcon} ${getProductIconClass(p.type)}`}>
                          {getProductTypeIcon(p.type)}
                        </div>
                        <span className={styles.reviewProductName}>{p.name}</span>
                        <span className={`${styles.badge} ${getBadgeClass(p.type)}`}>{p.type}</span>
                        <span className={`${styles.badge} ${styles.badgeHotel}`}>
                          <BedDouble size={10} /> PER ROOM
                        </span>
                        <span className={styles.productMetaItem}>
                          {p.pnrLabel}: {p.pnr}
                        </span>
                        <span className={styles.reviewProductAmount}>
                          {fmt(getProductPayAmount(p))}
                        </span>
                      </div>
                      {(p.rooms || []).map((r, idx) => {
                        const roomAmt = parseFloat(roomAmounts[`${p.id}-room-${r.id}`] || 0);
                        return (
                          <div key={r.id} className={styles.reviewTravelerRow}>
                            <div className={styles.reviewTravelerInfo}>
                              <div className={styles.roomIcon}>
                                <BedDouble size={14} />
                              </div>
                              <div>
                                <div className={styles.travelerName}>
                                  Room {idx + 1}{r.label ? ` — ${r.label}` : ""}
                                </div>
                                {r.board && <span className={styles.roomBoard}>{r.board}</span>}
                              </div>
                            </div>
                            <span className={styles.reviewTravelerAmount}>{fmt(roomAmt)}</span>
                          </div>
                        );
                      })}
                      {travelers.length > 0 && (
                        <div className={styles.roomTravelersInfo}>
                          <div className={styles.roomTravelersTitle}>
                            <Users size={12} /> Travelers — for information only, no amount
                            allocated per traveler
                          </div>
                          <div className={styles.roomTravelerChips}>
                            {travelers.map((t) => (
                              <span key={t.id} className={styles.roomTravelerChip}>
                                <span className={`${styles.avatar} ${getAvatarClass(t.type)}`}>
                                  {renderInitials(t.name)}
                                </span>
                                {t.name}
                                <span className={`${styles.travelerTypeBadge} ${getTravelerBadgeClass(t.type)}`}>
                                  {t.type || "Adult"}
                                </span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <div key={p.id} className={styles.reviewProductCard}>
                    <div className={styles.reviewProductHeader}>
                      <div
                        className={`${styles.productIcon} ${getProductIconClass(p.type)}`}
                      >
                        {getProductTypeIcon(p.type)}
                      </div>
                      <span className={styles.reviewProductName}>
                        {p.name}
                      </span>
                      <span className={`${styles.badge} ${getBadgeClass(p.type)}`}>
                        {p.type}
                      </span>
                      <span className={styles.productMetaItem}>
                        {p.pnrLabel}: {p.pnr}
                      </span>
                      <span className={styles.reviewProductAmount}>
                        {fmt(getProductPayAmount(p))}
                      </span>
                    </div>
                    {travelers.length > 0 &&
                      travelers.map((t) => {
                        const travelAmt = parseFloat(
                          travelerAmounts[`${p.id}-${t.id}`] || 0
                        );
                        return (
                          <div
                            key={t.id}
                            className={styles.reviewTravelerRow}
                          >
                            <div className={styles.reviewTravelerInfo}>
                              <div
                                className={`${styles.avatar} ${getAvatarClass(t.type)}`}
                              >
                                {renderInitials(t.name)}
                              </div>
                              <div>
                                <div className={styles.travelerName}>
                                  {t.name}
                                </div>
                                <span
                                  className={`${styles.travelerTypeBadge} ${getTravelerBadgeClass(t.type)}`}
                                >
                                  {t.type || "Adult"}
                                </span>
                              </div>
                            </div>
                            <span className={styles.reviewTravelerAmount}>
                              {fmt(travelAmt)}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                );
              })}
            </div>

            {/* Attachments summary in review */}
            {attachments.length > 0 && (
              <div className={styles.attachments}>
                <h4 className={styles.attachmentsTitle}>
                  <Upload size={14} /> Attachments
                  <span className={styles.attachmentCount}>{attachments.length}</span>
                </h4>
                <div className={styles.attachmentList}>
                  {attachments.map((a) => (
                    <div key={a.id} className={styles.attachmentItem}>
                      <div className={styles.attachmentIcon}>
                        <FileText size={16} />
                      </div>
                      <div className={styles.attachmentInfo}>
                        <span className={styles.attachmentName}>{a.name}</span>
                        <span className={styles.attachmentSize}>{formatFileSize(a.size)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right sidebar: Payment Summary */}
          <div className={styles.reviewSidebar}>
            <div className={styles.paymentSummary}>
              <div className={styles.paymentSummaryHeader}>
                <h4 className={styles.paymentSummaryTitle}>Payment Summary</h4>
              </div>
              <div className={styles.paymentSummaryBody}>
                <div className={styles.paymentSummaryRow}>
                  <span className={styles.paymentSummaryLabel}>Supplier</span>
                  <span className={styles.paymentSummaryValue}>
                    {activeSupplier?.name || "—"}
                  </span>
                </div>
                <div className={styles.paymentSummaryRow}>
                  <span className={styles.paymentSummaryLabel}>
                    Payment Type
                  </span>
                  <span className={styles.paymentSummaryValue}>
                    {form.paymentType}
                  </span>
                </div>
                <div className={styles.paymentSummaryRow}>
                  <span className={styles.paymentSummaryLabel}>
                    Payment Status
                  </span>
                  <span className={styles.paymentSummaryValue}>
                    <span
                      className={`${styles.statusBadge} ${getStatusBadgeClass(form.status)}`}
                    >
                      {form.status}
                    </span>
                  </span>
                </div>
                <div className={styles.paymentSummaryRow}>
                  <span className={styles.paymentSummaryLabel}>
                    Payment Method
                  </span>
                  <span className={styles.paymentSummaryValue}>
                    {form.method || "—"}
                  </span>
                </div>
                <div className={styles.paymentSummaryRow}>
                  <span className={styles.paymentSummaryLabel}>
                    Payment Date
                  </span>
                  <span className={styles.paymentSummaryValue}>
                    {formatDate(form.date)}
                  </span>
                </div>
                {accountLabel !== "—" && (
                  <div className={styles.paymentSummaryRow}>
                    <span className={styles.paymentSummaryLabel}>Account / IBAN</span>
                    <span className={styles.paymentSummaryValue}>
                      {accountLabel}
                    </span>
                  </div>
                )}
                <div className={styles.paymentSummaryRow}>
                  <span className={styles.paymentSummaryLabel}>
                    Reference / ID
                  </span>
                  <span className={styles.paymentSummaryValue}>
                    {form.reference || "—"}
                  </span>
                </div>
                <div className={styles.paymentSummaryRow}>
                  <span className={styles.paymentSummaryLabel}>Currency</span>
                  <span className={styles.paymentSummaryValue}>
                    {form.currency}
                  </span>
                </div>
                {form.notes && (
                  <div className={styles.paymentSummaryNotes}>
                    <span className={styles.paymentSummaryNotesLabel}>
                      Notes
                    </span>
                    <p className={styles.paymentSummaryNotesText}>
                      {form.notes}
                    </p>
                  </div>
                )}
              </div>

              {/* Totals box */}
              <div className={styles.paymentTotalBox}>
                <div className={styles.paymentTotalRow}>
                  <span className={styles.paymentTotalLabel}>
                    Products Selected
                  </span>
                  <span className={styles.paymentTotalValue}>
                    {selectedProducts.length}
                  </span>
                </div>
                <div className={styles.paymentTotalRow}>
                  <span className={styles.paymentTotalLabel}>
                    Travelers Selected
                  </span>
                  <span className={styles.paymentTotalValue}>
                    {totalTravelers}
                  </span>
                </div>
                <div className={styles.paymentTotalDivider} />
                <div className={styles.paymentTotalRow}>
                  <span className={styles.paymentTotalAmountLabel}>
                    Total Payment Amount
                  </span>
                  <span className={styles.paymentTotalAmountValue}>
                    {fmt(parseFloat(form.amount) > 0 ? parseFloat(form.amount) : totalPayment)}
                  </span>
                </div>
              </div>

              {/* Confirmation message */}
              <div className={styles.confirmMessage}>
                <div className={styles.confirmMessageIcon}>
                  <Check size={14} />
                </div>
                <span className={styles.confirmMessageText}>
                  Payment is ready to be confirmed. Review the details above and
                  click "Confirm Supplier Payment" to proceed.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* ===================================================================
     STEPPER HEADER
     =================================================================== */

  const renderStepper = () => (
    <div className={styles.stepper}>
      {STEPS.map((s, idx) => {
        const isDone = s.id < step;
        const isActive = s.id === step;
        const circleClass = isDone
          ? styles.stepCircleDone
          : isActive
          ? styles.stepCircleActive
          : styles.stepCircleUpcoming;
        const labelClass = isDone
          ? styles.stepLabelDone
          : isActive
          ? styles.stepLabelActive
          : styles.stepLabelUpcoming;

        return (
          <Fragment key={s.id}>
            <div
              className={`${styles.stepItem} ${isDone || isActive ? styles.stepItemClickable : ""}`}
              onClick={() => goToStep(s.id)}
              role="button"
              tabIndex={0}
            >
              <div className={`${styles.stepCircle} ${circleClass}`}>
                {isDone ? <Check size={16} /> : s.id}
              </div>
              <span className={`${styles.stepLabel} ${labelClass}`}>
                {s.label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div
                className={`${styles.stepLine} ${isDone ? styles.stepLineDone : isActive ? styles.stepLineActive : ""}`}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );

  /* ===================================================================
     FOOTER
     =================================================================== */

  const renderFooter = () => {
    if (step === 1) {
      return (
        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
          <div className={styles.footerRight}>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={goNext}
              disabled={realSuppliers.length === 0}
            >
              Next: Select Products <ArrowRight size={14} />
            </button>
          </div>
        </div>
      );
    }

    if (step === 2) {
      return (
        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            <button
              type="button"
              className={styles.btnBack}
              onClick={goBack}
            >
              <ArrowLeft size={14} /> Back
            </button>
          </div>
          <div className={styles.footerRight}>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={goNext}
              disabled={selectedProducts.length === 0 || hasAmountErrors}
            >
              Continue to Review <ArrowRight size={14} />
            </button>
          </div>
        </div>
      );
    }

    // Step 3
    return (
      <div className={styles.footer}>
        <div className={styles.footerLeft}>
          <button
            type="button"
            className={styles.btnBack}
            onClick={goBack}
          >
            <ArrowLeft size={14} /> Back
          </button>
          <button
            type="button"
            className={styles.btnDraft}
            onClick={handleDraft}
            disabled={saving}
          >
            <FileText size={14} /> Save as Draft
          </button>
        </div>
        <div className={styles.footerRight}>
          <button
            type="button"
            className={styles.btnEditSelection}
            onClick={() => setStep(2)}
          >
            <Eye size={14} /> Edit Selection
          </button>
          <button
            type="button"
            className={`${styles.btnPrimary} ${styles.btnConfirm}`}
            onClick={handleConfirm}
            disabled={saving || hasAmountErrors}
          >
            {saving ? (
              <>
                <span className={styles.spinner}>
                  <ArrowRight size={14} />
                </span>
                {editData ? "Updating…" : "Confirming…"}
              </>
            ) : (
              <>
                <Check size={14} />{" "}
                {editData
                  ? "Update Supplier Payment"
                  : "Confirm Supplier Payment"}
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  /* ===================================================================
     RENDER
     =================================================================== */

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={`${styles.wizard} ${step === 2 ? styles.wizardStep2 : ""} ${step === 3 ? styles.wizardWide : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerInfo}>
            <h3>
              {editData ? "Edit Supplier Payment" : "Add Supplier Payment"}
            </h3>
            <p>
              {editData
                ? "Update the details of this supplier payment."
                : "Record a payment to a supplier. Complete each step to finalize."}
            </p>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </div>

        {/* Stepper */}
        {renderStepper()}

        {/* Body */}
        <div className={styles.body}>
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>

        {/* Footer */}
        {renderFooter()}
      </div>
    </div>
  );
};

export default SupplierPaymentWizard;
